import React, { useEffect, useMemo, useState, useRef } from "react";
// 1. Core
import Highcharts from "highcharts/highstock";

// 2. Auto-init built-in modules on import
import "highcharts/highcharts-more";
import "highcharts/modules/heatmap";
import "highcharts/modules/sunburst";
import "highcharts/modules/sankey";
import "highcharts/modules/wordcloud";
import "highcharts/modules/treemap";
import "highcharts/modules/organization";
import "highcharts/modules/exporting";
import "highcharts/modules/export-data";
import "highcharts/modules/offline-exporting";
import "highcharts/modules/accessibility";
import "highcharts/modules/venn";
import "highcharts/highcharts-3d"; // 3D is now auto-registered
import "highcharts/modules/dumbbell"; // dumbbell auto-registered
import "highcharts/modules/lollipop"; // lollipop auto-registered
import "highcharts/modules/coloraxis"; // For color axis functionality in TreeMap
// import "highcharts/themes/adaptive"; // For automatic light/dark mode adaptation

// 3. External plugins still require manual init
import HC_customEvents from "highcharts-custom-events";
// 4. Apply global options
import HighchartOptions from "./highcharts.options";

import { useDispatch, useSelector } from "react-redux";
import hide_temporary from "../../../../assets/Hide_Temporary.svg";
import hide_permanent from "../../../../assets/Hide_Permanantly.svg";
import hide_permanent_brand from "../../../../assets/Hide_Permanently_Brand.svg";
import InfoIcon from "../../../../assets/Info_Icon.svg";
import { setWidgetMakerGraphData } from "../../../../redux/actions/WidgetMaker/WidgetAPI";
import {
  getPie,
  getLine,
  getMultiLine,
  getArea,
  getMultiArea,
  getSunburst,
  getBarChart,
  getStackBarChart,
  getHeat,
  getScatter,
  getQuadrant,
  getCombination,
  getWordcloud,
  getOrganization,
  getMultiplePie,
  getSankey,
  getTreeMap,
} from "./graphFunction";
import {
  setHighChartDeepDive,
  setHighChartRightClick,
  setOptionHighChart,
} from "../../../../redux/actions/HighChart";
import {
  setDeepDiveInfluencersObject,
  setDeepDiveMentionsObject,
  setDeepDiveObject,
  set_WM_DeepDive,
} from "../../../../redux/actions/WidgetMaker/WidgetMaker";
import { getDeepDive } from "../../../../redux/actions/WidgetMaker/WidgetAPI";
import {
  callNotification,
  deepCopy,
  getAllDeepDiveDashboardFilters,
  getAllFilters,
  getAllShareFilters,
  getBrands,
  getCustomizedDate,
  getGlobalBrands,
  getPaneIndex,
  getTemporaryWordWidgetId,
  getWidgetGraphObject,
  getWindowLocationPath,
  useQuery,
} from "../../../../redux/constants";
import moment from "moment";
import {
  setDeepDiveFlag,
  setDeepDiveInfoLoader,
  setIsDetailedInsightVisible,
  setMentionPageData,
  setTabClick,
} from "../../../../redux/actions/DeepDive";
import {
  setisDeepDive,
  setSelectedDeepdiveWidget,
} from "../../../../redux/actions/dashboard/Tab1";
import Tooltip from "antd/lib/tooltip";
import { setWordcloudModifyWords } from "../../../../redux/actions/ApiCall/apiCall";
import { setTemporaryWords } from "../../../../redux/actions/Attribute";
import {
  setAggregateData,
  setGraphData,
} from "../../../../redux/actions/ApiCall";
import { setPageChart } from "../../../../redux/actions/WidgetMaker/WidgetAPI";
import { setWidgetData } from "../../../../redux/actions/dashboard/Tab1";

import IsEmpty from "../../IsEmpty";
import isEqual from "react-fast-compare";
import HighChartrender from "./HighChartrender";
import { setShareSelectedDeepdiveWidget } from "../../../../redux/actions/share/Share";

import {
  getAllWidgetScreenFilters,
  getUniqueValues,
} from "../../../../redux/constants/commandCenterConstants";
import { setCommandCenterWordcloudModifyWords } from "../../../../redux/actions/commandCenter/commandCenterApi";
import {
  setClearScreenWidgetGraphData,
  setScreenUpdatedData,
  setWidgetXAxisAttribute,
  updateScreenWidgetPropdataWithClearGraphData,
  setScreenIsDeepDive,
  setSelectedDeepdiveWidgetInScreen,
  setCategoryScreenFilters,
  setDateAggregationLoader,
} from "../../../../redux/actions/commandCenter/screens/screen";
import { HideKeywordModal } from "./HideKeywordModal";
import { benchmarkAggregation } from "../../../../redux/constants/enums";
import { setIsDeepDiveInDashboardTemplateData } from "../../../../redux/actions/DeepDiveDashboard/DeepDiveDashboard";

// Add dynamic font sizing for treemap data labels
Highcharts?.addEvent(Highcharts?.Series, "drawDataLabels", function () {
  if (this?.type === "treemap") {
    // Skip custom logic during export
    if (this?.chart?.renderer?.forExport || this?.chart?.forExport) return;
    this.points.forEach((point) => {
      // Skip parent nodes - they'll be handled by the other event handler
      if (
        point?.node &&
        point?.shapeArgs &&
        !(
          point?.node?.children?.length > 0 ||
          (point?.node?.level === 1 && !point?.node?.isLeaf) ||
          point?.isParent
        )
      ) {
        const area = point?.shapeArgs?.width * point?.shapeArgs?.height;
        const minSize = 6; // Lower minimum font size
        const maxSize = 24; // Smaller maximum font size

        // Get label text
        const labelText = point?.dataLabel?.textStr || "";
        const linesArray = labelText?.split(/<br\s*\/?>|\n/);
        const lines = linesArray?.length;

        // Calculate max line length instead of average
        let maxLineLength = 0;
        let firstLineLength = 0;
        let totalChars = 0;

        linesArray?.forEach((line, index) => {
          const lineLength = line?.length;
          totalChars += lineLength;

          // Store first line length separately
          if (index === 0) {
            firstLineLength = lineLength;
          }

          // Track the maximum line length
          if (lineLength > maxLineLength) {
            maxLineLength = lineLength;
          }
        });

        // Average chars per line (for reference)
        const avgCharsPerLine = totalChars / lines;

        // Aspect ratio to handle wide vs tall boxes better
        const aspectRatio = point?.shapeArgs?.width / point?.shapeArgs?.height;
        const isWide = aspectRatio > 1.5;
        const isTall = aspectRatio < 0.67;

        // Better calculation that gives more weight to longest line
        // and special consideration to first line (often the title)
        let calculatedSize;

        // Weighted calculation that prioritizes the max line length
        // and gives special consideration to first line length
        const weightedLineLength = Math.max(
          maxLineLength,
          firstLineLength * 1.2 // First line gets 20% more weight as it's often a title
        );

        if (isWide) {
          // For wide boxes, prioritize width constraints
          calculatedSize =
            Math.sqrt(area) / (3 + lines * 0.6 + weightedLineLength * 0.08);
        } else if (isTall) {
          // For tall boxes, prioritize height constraints
          calculatedSize =
            Math.sqrt(area) / (3 + lines * 0.8 + weightedLineLength * 0.05);
        } else {
          // For balanced boxes
          calculatedSize =
            Math.sqrt(area) / (3 + lines * 0.7 + weightedLineLength * 0.06);
        }

        // Additional reduction for very long lines or many lines
        if (maxLineLength > 25) {
          calculatedSize *= 0.9;
        }
        if (lines > 3) {
          calculatedSize *= 0.9;
        }

        calculatedSize = Math.max(minSize, Math.min(maxSize, calculatedSize));

        // Set the options via dlOptions
        if (!point.dlOptions) {
          point.dlOptions = {};
        }

        if (!point.dlOptions.style) {
          point.dlOptions.style = {};
        }

        // Set font size and other styles through dlOptions
        point.dlOptions.style.fontSize = calculatedSize + "px";
        point.dlOptions.style.lineHeight = Math.min(1.2, 1 + 0.1 / lines) + "";
        point.dlOptions.style.whiteSpace = "pre-line";
        point.dlOptions.style.textOverflow = "ellipsis";

        // Also update already rendered labels for immediate effect
        if (point?.dataLabel) {
          if (point.dataLabel.css) {
            point.dataLabel.css({
              fontSize: calculatedSize + "px",
              lineHeight: Math.min(1.2, 1 + 0.1 / lines) + "",
              whiteSpace: "pre-line",
              textOverflow: "ellipsis",
            });
          }

          if (point.dataLabel.element) {
            point.dataLabel.element.style.fontSize = calculatedSize + "px";
            point.dataLabel.element.style.lineHeight =
              Math.min(1.2, 1 + 0.1 / lines) + "";
            point.dataLabel.element.style.whiteSpace = "pre-line";
            point.dataLabel.element.style.textOverflow = "ellipsis";
            point.dataLabel.element.style.overflow = "hidden";

            // For multiline labels, add proper overflow handling
            if (lines > 1) {
              point.dataLabel.element.style.display = "-webkit-box";
              point.dataLabel.element.style.webkitBoxOrient = "vertical";
              point.dataLabel.element.style.webkitLineClamp = Math.min(
                lines,
                4
              ); // Limit to max 4 lines
            }
          }
        }
      }
    });
  }
});

// Add custom event handler for treemap parent headers to fix width issues using dlOptions
Highcharts?.addEvent(Highcharts?.Series, "afterTranslate", function () {
  if (this?.type === "treemap") {
    const series = this;
    // Skip custom logic during export
    if (this?.chart?.renderer?.forExport || this?.chart?.forExport) return;

    this?.points?.forEach(function (point) {
      // Check if this is a parent node
      if (
        point?.node &&
        (point?.node?.children?.length > 0 ||
          point?.node?.level === 1 ||
          point?.isParent)
      ) {
        // Create dlOptions if it doesn't exist
        if (!point.dlOptions) {
          point.dlOptions = {};
        }

        // Set alignment and styling options with fixed small font size for parent nodes
        point.dlOptions = {
          ...point.dlOptions,
          verticalAlign: "top",
          align: "center",
          style: {
            ...(point.dlOptions.style || {}),
            fontSize: "0.9em",
            fontWeight: "bold",
            textTransform: "uppercase",
            textOutline: "none",
            width: "100%",
            textAlign: "center",
          },
          padding: 4,
          y: 4,
          backgroundColor: point?.color || point?.series?.color,
          inside: true,
          crop: false,
          overflow: "none",
        };

        // Set position based on shape args for proper sizing
        if (point.shapeArgs) {
          // Calculate position to ensure label is at the top of the box
          point.dlOptions.x = 0;
          point.dlOptions.y = 4;
        }

        // Also update already rendered labels for immediate effect
        if (point?.dataLabel && point.dataLabel.element) {
          point.dataLabel.element.style.fontSize = "0.9em"; // Ensure it's applied to rendered elements
          point.dataLabel.element.style.fontWeight = "bold";
          point.dataLabel.element.style.textTransform = "uppercase";
        }
      }
    });
  }
});

// 2. External plugins still require manual init
HC_customEvents(Highcharts);
// 4. Register global options last
HighchartOptions(Highcharts); // <-- add this after all module registrations

const HighCharts = ({
  dashboard,
  single,
  title,
  colors,
  type,
  xaxis,
  yaxis,
  style,
  stacked,
  id,
  legend_position,
  show_legend,
  show_horizontal_view,
  chartGlobalDataLabel,
  chartShowDetailsOnCard,
  chartShow3d,
  chart3dOption,
  chartAxisLabel,
  chartDataLabelInPercent,
  chartGradientColor,
  chartXaxisDataLabel,
  chartMarkers,
  showAsMedian,
  xAxisChartType,
  yAxisChartType,
  xAxisScale,
  yAxisScale,
  percent,
  split,
  y_series2,
  chartGlobalDataStacked,
  yAxisMode,
  metadata,
  span_data,
  section_index,
  widget_index,
  differential,
  log,
  scaleFromZeroToOne,
  agentActivit,
  PdfDownloadStatus,
  ticket_widget_id,
  ticket_chart_type,
  template_widget,
  ticket_status_duration,
  trends_type,
  AiInsights,
  deepDive,
  fontFamily,
  metaWidgetGraphData,
  isSurveyCardChart,
  dateAggregationOfThisGraph,
  survey_card_post_text,
  chartStyles,
}) => {
  let query = useQuery();

  const dispatch = useDispatch();
  const personalizationSettingsObj = useSelector(
    (state) => state?.PersonalizationSetting
  );
  const updatedGraphData = useSelector(
    (state) => state.Widget.updatedGraphData
  );
  const refTooltip = useRef(null);
  const shareCategory = useSelector((state) => state.ApiCall.shareCategory);

  const global_dashboard_brands = useSelector(
    (state) => state.AuthParams.global_dashboard_brands
  );
  const global_dashboard_single_brands = useSelector(
    (state) => state.AuthParams.global_dashboard_single_brands
  );
  const temporary_words = useSelector(
    (state) => state.attributes.temporary_words
  );

  const brandlogicalgroup = useSelector(
    (state) => state.ApiCall.brandlogicalgroup?.data?.result
  );
  const PersonalizationSettingObj = useSelector(
    (state) => state?.PersonalizationSetting
  );
  const activeKey = useSelector((state) => state?.Tab1?.activeKey);
  const shareBrands = useSelector((state) => state.ApiCall.shareBrands);
  const shareableLinkDeepDiveStatus = useSelector(
    (state) => state.ApiCall.shareableLinkDeepDiveStatus
  );
  const footerValue = useSelector((state) => state.Section.footerValue);
  const brands = useSelector(
    (state) => state.ApiCall.brands?.data?.result?.brands
  );
  const panes = useSelector((state) => state?.Tab1?.panes);
  const screen = useSelector((state) => state?.Screens?.screen);
  const screenRefreshEvent = useSelector(
    (state) => state?.Screens?.refreshEvent
  );
  const deepdive_object = useSelector(
    (state) => state?.WidgetMaker?.deepdive_object
  );
  let index = getPaneIndex(panes, activeKey);

  const isshowSideBar = useSelector((state) => state.WidgetMaker.isshowSideBar);
  const opData = useSelector((state) => state.Highchart.Options);
  const authParams = useSelector((state) => state.Authentication.authParams);
  const All_WM_attributes = useSelector(
    (state) => state.WidgetMaker.All_WM_attributes
  );
  const WM_attributes = useSelector((state) => state.WidgetMaker.WM_attributes);
  const allAttributes = useSelector(
    (state) => state?.attributes?.allAttributes
  );
  const isPPTModalOpen = useSelector((state) => state.PPT.isPPTModalOpen);
  let attribute = All_WM_attributes ? All_WM_attributes : allAttributes;
  const widgetGraphConfig = useSelector(
    (state) => state?.GraphConfig?.widgetGraphConfig
  );
  const dashboardPdfdata = useSelector(
    (state) => state.ApiCall.dashboardPdfdata
  );
  const competitorBrands = useSelector(
    (state) => state.WidgetAPI.competitorBrands?.data?.result
  );
  //const widgetPreview = useSelector((state) => state.WidgetAPI.widgetPreview);
  const isWidgetEdit = useSelector((state) => state.Filter.isWidgetEdit);
  const aiLogScale = useSelector(
    (state) => state.Welcome.graphDetail.aiLogScale
  );
  // const PdfDownloadStatus = useSelector(
  // 	(state) => state.PdfDownloadData.PdfDownloadStatus
  // );
  //const [changeChart_type, setchangeChart_type] = useState(null);
  const [currentSelectedGraph, setCurrentSelectedGraph] = useState(null);
  const Section = useSelector((state) => state.Section.addSection);
  const highChartDeepDive = useSelector(
    (state) => state.Highchart.highChartDeepDive
  );
  const highChartRightClick = useSelector(
    (state) => state.Highchart.highChartRightClick
  );
  const dashboardTrendsFlag = useSelector(
    (state) => state.WidgetAPI.dashboardTrendsFlag
  );
  // let widgetCondition =
  //   opData && Object.keys(opData)?.findIndex((el) => el[1]?.chart);
  const templateDetails = useSelector((state) => state.Section.templateDetails);
  const selectLogicalBrandValue = useSelector(
    (state) => state?.Logicalgroup.selectLogicalBrandValue
  );
  const global_dashboard_duration = useSelector(
    (state) => state.AuthParams.global_dashboard_duration
  );
  const shareTemplateData = useSelector(
    (state) => state.ApiCall.shareTemplateData
  );
  const graphConditionConfig = useSelector(
    (state) => state?.apiCall?.graphConditions
  );
  const isScheduleReportOpen = useSelector(
    (state) => state?.ApiCall?.is_schedule_report_open
  );
  const workspace_privileges = useSelector(
    (state) => state.ApiCall.workspace_privileges
  );
  const share_workspace_privileges = useSelector(
    (state) => state.ApiCall.share_workspace_privileges
  );
  const deepDiveFlag = useSelector((state) => state?.DeepDive?.deepDiveFlag);
  const [xPosition, setxPosition] = useState(0);
  const [yPosition, setyPosition] = useState(0);
  const [keywordData, setkeywordData] = useState({});
  const [load, setLoad] = useState(false);
  const sharedDuration = useSelector((state) => state.Share.sharedDuration);

  const deepdiveDashboardData = useSelector(
    (state) => state?.DeepDiveDashboard?.dashboardData
  );
  const deepdiveDashboardTemplateData = useSelector(
    (state) => state?.DeepDiveDashboard?.dashboardTemplateData
  );

  let dashboardCompetitorList = panes[index]?.brands
    ? panes[index]?.brands?.columns
      ? panes[index]?.brands?.columns[0]?.competitorList
      : panes[index]?.brands[0]?.competitorList
    : selectLogicalBrandValue?.global_dashboard?.data?.columns &&
      selectLogicalBrandValue?.global_dashboard?.data?.columns[0]
        ?.competitorList;
  let widgetMakerCompetitorList =
    widgetGraphConfig?.brands && widgetGraphConfig?.brands[0]?.competitor;
  let templateDetailsCompetitorList =
    templateDetails?.templateBrands?.columns &&
    templateDetails?.templateBrands?.columns[0]?.competitorList;
  let pdfCompititorList =
    dashboardPdfdata &&
    dashboardPdfdata?.template_metadata &&
    dashboardPdfdata?.template_metadata?.comp_brand_list &&
    dashboardPdfdata?.template_metadata?.comp_brand_list?.length > 0;

  // dashboardCompetitorList,
  // widgetMakerCompetitorList,
  // templateDetailsCompetitorList)
  const [tempModal, setTempModal] = useState(false);
  const [permModal, setPermModal] = useState(false);
  const [brandModal, setBrandModal] = useState(false);
  const [modalCheckboxValue, setModalCheckboxValue] = useState(false);
  let pathName = getWindowLocationPath()?.toLowerCase();
  // adding accent colors to the charts
  const selectedPalette = PersonalizationSettingObj?.paletteList?.find(
    (palette) => palette.isSelected === true
  );
  const accentColors = selectedPalette?.AccentColors;

  const isDeepDiveDashboardView = useMemo(() => {
    let pathName = getWindowLocationPath()?.toLowerCase();
    return (
      (authParams?.screen_id
        ? screen?.isDeepDiveDashboard
          ? true
          : false
        : shareableLinkDeepDiveStatus
        ? shareTemplateData?.isDeepDiveDashboard
          ? true
          : false
        : !authParams?.screen_id &&
          !shareBrands &&
          (pathName === "dashboard" || pathName === "pin-dashboard") &&
          !panes?.[getPaneIndex(panes, activeKey)]?.is_widget_maker_open_obj
            ?.open_flag &&
          panes?.[getPaneIndex(panes, activeKey)]?.isDeepDiveDashboard
        ? true
        : false) && !deepdiveDashboardTemplateData?.isDeepDive
    );
  }, [
    screen,
    shareTemplateData,
    panes,
    authParams,
    deepdiveDashboardTemplateData,
  ]);

  const isDeepDiveView = authParams?.screen_id
    ? screen?.isDeepDive || screen?.isDeepDiveDashboard
      ? screen?.isDeepDive || screen?.isDeepDiveDashboard
      : false
    : shareableLinkDeepDiveStatus
    ? shareTemplateData?.isDeepDive || shareTemplateData?.isDeepDiveDashboard
      ? shareTemplateData?.isDeepDive || shareTemplateData?.isDeepDiveDashboard
      : false
    : panes[getPaneIndex(panes, activeKey)]?.isDeepDive ||
      panes[getPaneIndex(panes, activeKey)]?.isDeepDiveDashboard
    ? panes[getPaneIndex(panes, activeKey)]?.isDeepDive ||
      panes[getPaneIndex(panes, activeKey)]?.isDeepDiveDashboard
    : false;
  //this function calls graphFunction for get{chart_type}chart functions
  const getChartDataObj = () => {
    if (
      (type == "horizontal-bar" ||
        type == "bar" ||
        type == "lollipop" ||
        Object.keys(updatedGraphData).findIndex((el) => el === "bar_" + id) >=
          0) &&
      !stacked
    ) {
      let index = Object.keys(updatedGraphData).findIndex(
        (el) => el === "bar_" + id
      );

      if (index >= 0) {
        //chart type change

        if (updatedGraphData["bar_" + id]?.selected_chart == "line") {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          if (single[0].data) {
            let index = getPaneIndex(panes, activeKey);
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "line",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartGlobalDataStacked: chartGlobalDataStacked,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              panes: panes,
              index: index,
              dashboard: dashboard,
              widget_name: metadata?.widget_name,
              ticket_chart_type: ticket_chart_type,
              section_index: section_index,
              widget_index: widget_index,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              ticket_status_duration: ticket_status_duration,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              isDeepDive: screen?.isDeepDive,
              dateAggregationOfThisGraph: dateAggregationOfThisGraph,
              deepdiveObject: deepdive_object,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiLine(
              graphObject,
              authParams,
              Highcharts,
              true
            );
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "line",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,

              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              screen: screen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getLine(graphObject, authParams);

            dispatch(setOptionHighChart(updateOptions));
          }
        } else if (updatedGraphData["bar_" + id]?.selected_chart == "area") {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};

          if (single[0].data) {
            let index = getPaneIndex(panes, activeKey);
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "area",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              chartGlobalDataStacked: chartGlobalDataStacked,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "area",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              accentColors: accentColors,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          }
        } else {
          let index = getPaneIndex(panes, activeKey);
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let graphObject = {
            single: single,
            title: title,
            //colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chart3dOption: chart3dOption,
            chartShow3d: chartShow3d,
            show_horizontal_view:
              metadata?.chart?.chart_settings?.show_horizontal_view,
            chartAxisLabel: chartAxisLabel,
            //chartXaxisDataLabel: chartXaxisDataLabel,
            //chartMarkers: chartMarkers,
            //xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            stacked: stacked,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            agentActivit: agentActivit,
            All_WM_attributes: All_WM_attributes,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            widgetGraphConfig: widgetGraphConfig,
            widget_name: metadata?.widget_name,
            metadata: metadata,
            section_index: section_index,
            widget_index: widget_index,
            panes: panes,
            index: index,
            dashboard: dashboard,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = getBarChart(graphObject, authParams, deepDive);
          dispatch(setOptionHighChart(updateOptions));
        }
      } else {
        let index = getPaneIndex(panes, activeKey);
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          single: single,
          title: title,
          //colors: colors,
          xaxis: xaxis,
          yaxis: yaxis,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          chartGlobalDataLabel: chartGlobalDataLabel,
          chart3dOption: chart3dOption,
          chartShow3d: chartShow3d,
          show_horizontal_view:
            metadata?.chart?.chart_settings?.show_horizontal_view,
          chartAxisLabel: chartAxisLabel,
          //chartXaxisDataLabel: chartXaxisDataLabel,
          //chartMarkers: chartMarkers,
          //xAxisScale: xAxisScale,
          yAxisScale: yAxisScale,
          style: style,
          stacked: stacked,
          aiLogScale: aiLogScale !== null ? aiLogScale : log,
          scaleFromZeroToOne: scaleFromZeroToOne,
          agentActivit: agentActivit,
          All_WM_attributes: All_WM_attributes,
          pdfDownloadStatus: PdfDownloadStatus
            ? PdfDownloadStatus
            : isPPTModalOpen,
          competitorBenchmark:
            dashboardCompetitorList ||
            widgetMakerCompetitorList ||
            templateDetailsCompetitorList ||
            pdfCompititorList,
          widgetGraphConfig: widgetGraphConfig,
          widget_name: metadata?.widget_name,
          metadata: metadata,
          section_index: section_index,
          widget_index: widget_index,
          panes: panes,
          index: index,
          dashboard: dashboard,
          section: Section,
          template_widget: template_widget,
          shareTemplateData: shareTemplateData,
          graphConditionConfig: graphConditionConfig,
          isScheduleReportOpen: isScheduleReportOpen,
          template_details: templateDetails,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };
        updateOptions[id] = getBarChart(graphObject, authParams, deepDive);
        dispatch(setOptionHighChart(updateOptions));
      }
    } else if (
      //stack bar
      (type === "horizontal-bar" ||
        type === "bar" ||
        type === "lollipop" ||
        Object.keys(updatedGraphData).findIndex((el) => el === "bar_" + id) >=
          0) &&
      stacked
    ) {
      let index = Object.keys(updatedGraphData).findIndex(
        (el) => el === "bar_" + id
      );
      if (index >= 0) {
        //chart type change

        if (updatedGraphData["bar_" + id]?.selected_chart == "line") {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};

          if (single[0].data) {
            let index = getPaneIndex(panes, activeKey);
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "line",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              chartGlobalDataStacked: chartGlobalDataStacked,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              panes: panes,
              index: index,
              dashboard: dashboard,
              widget_name: metadata?.widget_name,
              ticket_chart_type: ticket_chart_type,
              section_index: section_index,
              widget_index: widget_index,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              ticket_status_duration: ticket_status_duration,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              isDeepDive: screen?.isDeepDive,
              dateAggregationOfThisGraph: dateAggregationOfThisGraph,
              deepdiveObject: deepdive_object,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiLine(
              graphObject,
              authParams,
              Highcharts,
              true
            );
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "line",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              screen: screen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getLine(graphObject, authParams);

            dispatch(setOptionHighChart(updateOptions));
          }
        } else if (updatedGraphData["bar_" + id]?.selected_chart == "area") {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};

          if (single[0].data) {
            let index = getPaneIndex(panes, activeKey);
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "area",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              chartGlobalDataStacked: chartGlobalDataStacked,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "area",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              accentColors: accentColors,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          }
        } else {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let index = getPaneIndex(panes, activeKey);
          let graphObject = {
            single: single,
            xaxis: xaxis,
            yaxis: yaxis,
            style: style,
            stacked: stacked,
            type: type,
            chart3dOption: chart3dOption,
            chartShow3d: chartShow3d,
            legend_position: legend_position,
            show_legend: show_legend,
            percent: percent,
            chartGlobalDataLabel: chartGlobalDataLabel,
            show_horizontal_view:
              metadata?.chart?.chart_settings?.show_horizontal_view,
            chartAxisLabel: chartAxisLabel,
            chartDataLabelInPercent: chartDataLabelInPercent,
            chartGradientColor: chartGradientColor,
            chartGlobalDataStacked: chartGlobalDataStacked,
            yAxisScale: yAxisScale,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            All_WM_attributes: All_WM_attributes,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            panes: panes,
            index: index,
            dashboard: dashboard,
            ticket_widget_id: ticket_widget_id,
            widgetGraphConfig: widgetGraphConfig,
            widget_name: metadata?.widget_name,
            metadata: metadata,
            ticket_chart_type: ticket_chart_type,
            section_index: section_index,
            widget_index: widget_index,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            ticket_status_duration: ticket_status_duration,
            isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = getStackBarChart(graphObject, authParams);
          dispatch(setOptionHighChart(updateOptions));
        }
      } else {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let index = getPaneIndex(panes, activeKey);
        let graphObject = {
          single: single,
          xaxis: xaxis,
          yaxis: yaxis,
          style: style,
          stacked: stacked,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          percent: percent,
          chartGlobalDataLabel: chartGlobalDataLabel,
          show_horizontal_view:
            metadata?.chart?.chart_settings?.show_horizontal_view,
          chartAxisLabel: chartAxisLabel,
          chart3dOption: chart3dOption,
          chartShow3d: chartShow3d,
          chartDataLabelInPercent: chartDataLabelInPercent,
          chartGradientColor: chartGradientColor,
          chartGlobalDataStacked: chartGlobalDataStacked,
          yAxisScale: yAxisScale,
          aiLogScale: aiLogScale !== null ? aiLogScale : log,
          scaleFromZeroToOne: scaleFromZeroToOne,
          All_WM_attributes: All_WM_attributes,
          pdfDownloadStatus: PdfDownloadStatus
            ? PdfDownloadStatus
            : isPPTModalOpen,
          competitorBenchmark:
            dashboardCompetitorList ||
            widgetMakerCompetitorList ||
            templateDetailsCompetitorList ||
            pdfCompititorList,
          panes: panes,
          index: index,
          dashboard: dashboard,
          ticket_widget_id: ticket_widget_id,
          widgetGraphConfig: widgetGraphConfig,
          widget_name: metadata?.widget_name,
          metadata: metadata,
          ticket_chart_type: ticket_chart_type,
          section_index: section_index,
          widget_index: widget_index,
          section: Section,
          template_widget: template_widget,
          shareTemplateData: shareTemplateData,
          graphConditionConfig: graphConditionConfig,
          ticket_status_duration: ticket_status_duration,
          isScheduleReportOpen: isScheduleReportOpen,
          template_details: templateDetails,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };
        updateOptions[id] = getStackBarChart(graphObject, authParams);

        dispatch(setOptionHighChart(updateOptions));
      }
    } else if (
      type === "line" ||
      Object.keys(updatedGraphData).findIndex((el) => el === "line_" + id) >= 0
    ) {
      let index = Object.keys(updatedGraphData).findIndex(
        (el) => el === "line_" + id
      );
      if (index >= 0) {
        //chart type change

        if (updatedGraphData["line_" + id]?.selected_chart == "bar") {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let index = getPaneIndex(panes, activeKey);
          let graphObject = {
            single: single,
            xaxis: xaxis,
            yaxis: yaxis,
            style: style,
            stacked: stacked,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            percent: percent,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chart3dOption: chart3dOption,
            chartShow3d: chartShow3d,
            show_horizontal_view:
              metadata?.chart?.chart_settings?.show_horizontal_view,
            chartAxisLabel: chartAxisLabel,
            chartDataLabelInPercent: chartDataLabelInPercent,
            chartGradientColor: chartGradientColor,
            chartGlobalDataStacked: false, // because we not allowed stacking from widget maker
            yAxisScale: yAxisScale,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            agentActivit: agentActivit,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            panes: panes,
            index: index,
            dashboard: dashboard,
            ticket_widget_id: ticket_widget_id,
            widgetGraphConfig: widgetGraphConfig,
            widget_name: metadata?.widget_name,
            metadata: metadata,
            ticket_chart_type: ticket_chart_type,
            section_index: section_index,
            widget_index: widget_index,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            ticket_status_duration: ticket_status_duration,
            isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = single[0]?.data
            ? getStackBarChart(graphObject, authParams)
            : getBarChart(graphObject, authParams, deepDive);
          dispatch(setOptionHighChart(updateOptions));
        } else if (updatedGraphData["line_" + id]?.selected_chart == "area") {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};

          if (single[0].data) {
            let index = getPaneIndex(panes, activeKey);
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "area",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              chartGlobalDataStacked: false,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "area",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              chartGlobalDataStacked: false,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              accentColors: accentColors,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          }
        } else {
          if (single[0].data) {
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let index = getPaneIndex(panes, activeKey);
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: type,
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              chartGlobalDataStacked: false,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              All_WM_attributes: All_WM_attributes,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              panes: panes,
              index: index,
              dashboard: dashboard,
              widget_name: metadata?.widget_name,
              ticket_chart_type: ticket_chart_type,
              section_index: section_index,
              widget_index: widget_index,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              ticket_status_duration: ticket_status_duration,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              isDeepDive: screen?.isDeepDive,
              dateAggregationOfThisGraph: dateAggregationOfThisGraph,
              deepdiveObject: deepdive_object,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiLine(
              graphObject,
              authParams,
              Highcharts
            );
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "line",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              All_WM_attributes: All_WM_attributes,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              screen: screen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getLine(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          }
        }
      } else {
        if (single[0]?.data) {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let index = getPaneIndex(panes, activeKey);
          let graphObject = {
            single: single,
            title: title,
            colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartGlobalDataStacked: false,
            chartAxisLabel: chartAxisLabel,
            chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            widgetGraphConfig: widgetGraphConfig,
            metadata: metadata,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            All_WM_attributes: All_WM_attributes,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            panes: panes,
            index: index,
            dashboard: dashboard,
            widget_name: metadata?.widget_name,
            ticket_chart_type: ticket_chart_type,
            section_index: section_index,
            widget_index: widget_index,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            ticket_status_duration: ticket_status_duration,
            isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            screen: screen,
            isDeepDive: screen?.isDeepDive,
            dateAggregationOfThisGraph: dateAggregationOfThisGraph,
            deepdiveObject: deepdive_object,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = getMultiLine(graphObject, authParams, Highcharts);
          dispatch(setOptionHighChart(updateOptions));
        } else {
          let index = getPaneIndex(panes, activeKey);
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let graphObject = {
            single: single,
            title: title,
            colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: "line",
            legend_position: legend_position,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartAxisLabel: chartAxisLabel,
            chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            widgetGraphConfig: widgetGraphConfig,
            metadata: metadata,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            All_WM_attributes: All_WM_attributes,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            widget_name: metadata?.widget_name,
            section_index: section_index,
            widget_index: widget_index,
            panes: panes,
            index: index,
            dashboard: dashboard,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            isScheduleReportOpen: isScheduleReportOpen,
            screen: screen,
            template_details: templateDetails,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = getLine(graphObject, authParams);
          dispatch(setOptionHighChart(updateOptions));
        }
      }
    } else if (
      type === "area" ||
      Object.keys(updatedGraphData).findIndex((el) => el === "area_" + id) >= 0
    ) {
      let index = Object.keys(updatedGraphData).findIndex(
        (el) => el === "area_" + id
      );
      if (index >= 0) {
        //chart type change

        if (updatedGraphData["area_" + id]?.selected_chart == "bar") {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let index = getPaneIndex(panes, activeKey);
          let graphObject = {
            single: single,
            xaxis: xaxis,
            yaxis: yaxis,
            style: style,
            stacked: stacked,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            percent: percent,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chart3dOption: chart3dOption,
            chartShow3d: chartShow3d,
            show_horizontal_view:
              metadata?.chart?.chart_settings?.show_horizontal_view,
            chartAxisLabel: chartAxisLabel,
            chartDataLabelInPercent: chartDataLabelInPercent,
            chartGradientColor: chartGradientColor,
            chartGlobalDataStacked: chartGlobalDataStacked,
            yAxisScale: yAxisScale,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            agentActivit: agentActivit,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            panes: panes,
            index: index,
            dashboard: dashboard,
            ticket_widget_id: ticket_widget_id,
            widgetGraphConfig: widgetGraphConfig,
            widget_name: metadata?.widget_name,
            metadata: metadata,
            ticket_chart_type: ticket_chart_type,
            section_index: section_index,
            widget_index: widget_index,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            ticket_status_duration: ticket_status_duration,
            isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = single[0]?.data
            ? getStackBarChart(graphObject, authParams)
            : getBarChart(graphObject, authParams, deepDive);
          dispatch(setOptionHighChart(updateOptions));
        } else if (updatedGraphData["area_" + id]?.selected_chart == "line") {
          if (single[0].data) {
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let index = getPaneIndex(panes, activeKey);
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "line",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartGlobalDataStacked: chartGlobalDataStacked,
              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              panes: panes,
              index: index,
              dashboard: dashboard,
              widget_name: metadata?.widget_name,
              ticket_chart_type: ticket_chart_type,
              section_index: section_index,
              widget_index: widget_index,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              ticket_status_duration: ticket_status_duration,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              isDeepDive: screen?.isDeepDive,
              dateAggregationOfThisGraph: dateAggregationOfThisGraph,
              deepdiveObject: deepdive_object,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiLine(
              graphObject,
              authParams,
              Highcharts,
              true
            );
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: "line",
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              pdfDownloadStatus: PdfDownloadStatus,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              screen: screen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getLine(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          }
        } else {
          if (single[0]?.data) {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: type,
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              chartGlobalDataStacked: chartGlobalDataStacked,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              All_WM_attributes: All_WM_attributes,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getMultiArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          } else {
            let index = getPaneIndex(panes, activeKey);
            let updateOptions = global.options
              ? { ...global?.options }
              : opData
              ? { ...opData }
              : {};
            let graphObject = {
              single: single,
              title: title,
              colors: colors,
              xaxis: xaxis,
              yaxis: yaxis,
              type: type,
              legend_position: legend_position,
              show_legend: show_legend,
              chartGlobalDataLabel: chartGlobalDataLabel,
              chartAxisLabel: chartAxisLabel,
              //chartXaxisDataLabel: chartXaxisDataLabel,
              chartMarkers: chartMarkers,
              //xAxisScale: xAxisScale,
              yAxisScale: yAxisScale,
              style: style,
              widgetGraphConfig: widgetGraphConfig,
              metadata: metadata,
              aiLogScale: aiLogScale !== null ? aiLogScale : log,
              scaleFromZeroToOne: scaleFromZeroToOne,
              All_WM_attributes: All_WM_attributes,
              pdfDownloadStatus: PdfDownloadStatus
                ? PdfDownloadStatus
                : isPPTModalOpen,
              competitorBenchmark:
                dashboardCompetitorList ||
                widgetMakerCompetitorList ||
                templateDetailsCompetitorList ||
                pdfCompititorList,
              widget_name: metadata?.widget_name,
              section_index: section_index,
              widget_index: widget_index,
              panes: panes,
              index: index,
              dashboard: dashboard,
              section: Section,
              template_widget: template_widget,
              shareTemplateData: shareTemplateData,
              graphConditionConfig: graphConditionConfig,
              isScheduleReportOpen: isScheduleReportOpen,
              template_details: templateDetails,
              screen: screen,
              accentColors: accentColors,
              isDeepDiveView: isDeepDiveView,
              isDeepDiveDashboardView: isDeepDiveDashboardView,
              deepdiveDashboardData: deepdiveDashboardData,
              deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
            };
            updateOptions[id] = getArea(graphObject, authParams);
            dispatch(setOptionHighChart(updateOptions));
          }
        }
      } else {
        if (single && single[0]?.data) {
          let index = getPaneIndex(panes, activeKey);
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let graphObject = {
            single: single,
            title: title,
            colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartAxisLabel: chartAxisLabel,
            //chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            //xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            chartGlobalDataStacked: chartGlobalDataStacked,
            widgetGraphConfig: widgetGraphConfig,
            metadata: metadata,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            All_WM_attributes: All_WM_attributes,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            widget_name: metadata?.widget_name,
            section_index: section_index,
            widget_index: widget_index,
            panes: panes,
            index: index,
            dashboard: dashboard,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = getMultiArea(graphObject, authParams);
          dispatch(setOptionHighChart(updateOptions));
        } else {
          let index = getPaneIndex(panes, activeKey);
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let graphObject = {
            single: single,
            title: title,
            colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartAxisLabel: chartAxisLabel,
            //chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            //xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            widgetGraphConfig: widgetGraphConfig,
            metadata: metadata,
            aiLogScale: aiLogScale !== null ? aiLogScale : log,
            scaleFromZeroToOne: scaleFromZeroToOne,
            All_WM_attributes: All_WM_attributes,
            pdfDownloadStatus: PdfDownloadStatus
              ? PdfDownloadStatus
              : isPPTModalOpen,
            competitorBenchmark:
              dashboardCompetitorList ||
              widgetMakerCompetitorList ||
              templateDetailsCompetitorList ||
              pdfCompititorList,
            widget_name: metadata?.widget_name,
            section_index: section_index,
            widget_index: widget_index,
            panes: panes,
            index: index,
            dashboard: dashboard,
            section: Section,
            template_widget: template_widget,
            shareTemplateData: shareTemplateData,
            graphConditionConfig: graphConditionConfig,
            isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            screen: screen,
            accentColors: accentColors,
            isDeepDiveView: isDeepDiveView,
            isDeepDiveDashboardView: isDeepDiveDashboardView,
            deepdiveDashboardData: deepdiveDashboardData,
            deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          };
          updateOptions[id] = getArea(graphObject, authParams);
          dispatch(setOptionHighChart(updateOptions));
        }
      }
    }
  };

  // only call in case of chart type changed from old awa
  useEffect(() => {
    if (!IsEmpty(updatedGraphData)) {
      getChartDataObj();
    }
  }, [updatedGraphData]);

  useEffect(() => {
    let basicGraphObject = {
      widgetGraphConfig: widgetGraphConfig,
      widget_name: metadata?.widget_name,
      metadata: metadata,
      section_index: section_index,
      widget_index: widget_index,
      panes: panes,
      index: index,
      dashboard: dashboard,
      section: Section,
      template_widget: template_widget,
      shareTemplateData: shareTemplateData,
      graphConditionConfig: graphConditionConfig,
      isScheduleReportOpen: isScheduleReportOpen,
      ticket_widget_id: ticket_widget_id,
      ticket_chart_type: ticket_chart_type,
      ticket_status_duration: ticket_status_duration,
      aiLogScale: aiLogScale !== null ? aiLogScale : log,
      scaleFromZeroToOne: scaleFromZeroToOne,
      All_WM_attributes: All_WM_attributes,
      trends_type: trends_type,
      pdfDownloadStatus: PdfDownloadStatus ? PdfDownloadStatus : isPPTModalOpen,
      competitorBenchmark:
        dashboardCompetitorList ||
        widgetMakerCompetitorList ||
        templateDetailsCompetitorList ||
        pdfCompititorList,
      isDeepDiveView: isDeepDiveView,
      isDeepDiveDashboardView: isDeepDiveDashboardView,
      deepdiveDashboardData: deepdiveDashboardData,
      deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
      chartStyles: chartStyles,
      chartShowDetailsOnCard: chartShowDetailsOnCard,
    };
    if (
      (dashboard && !opData?.[id] && !deepDiveFlag) ||
      !dashboard ||
      deepDive ||
      (isDeepDiveDashboardView && !opData?.[id])
    ) {
      if (
        (type == "horizontal-bar" || type == "bar" || type === "lollipop") &&
        !stacked &&
        IsEmpty(updatedGraphData)
      ) {
        let updateOptions = global.options
          ? { ...global.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          ...basicGraphObject,
          single: single,
          title: title,
          //colors: colors,
          xaxis: xaxis,
          yaxis: yaxis,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          chartGlobalDataLabel: chartGlobalDataLabel,
          chart3dOption: chart3dOption,
          chartShow3d: chartShow3d,
          show_horizontal_view:
            metadata?.chart?.chart_settings?.show_horizontal_view,
          chartAxisLabel: chartAxisLabel,
          //chartXaxisDataLabel: chartXaxisDataLabel,
          //chartMarkers: chartMarkers,
          //xAxisScale: xAxisScale,
          yAxisScale: yAxisScale,
          style: style,
          stacked: stacked,
          // aiLogScale: aiLogScale !== null ? aiLogScale : log,
          agentActivit: agentActivit,
          // All_WM_attributes: All_WM_attributes,
          // pdfDownloadStatus: PdfDownloadStatus
          //   ? PdfDownloadStatus
          //   : isPPTModalOpen,
          // competitorBenchmark:
          //   dashboardCompetitorList ||
          //   widgetMakerCompetitorList ||
          //   templateDetailsCompetitorList ||
          //   pdfCompititorList,
          // widgetGraphConfig: widgetGraphConfig,
          // widget_name: metadata?.widget_name,
          // metadata: metadata,
          // section_index: section_index,
          // widget_index: widget_index,
          // panes: panes,
          // index: index,
          // dashboard: dashboard,
          // section: Section,
          // template_widget: template_widget,
          // shareTemplateData: shareTemplateData,
          // graphConditionConfig: graphConditionConfig,
          // isScheduleReportOpen: isScheduleReportOpen,
          template_details: templateDetails,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getBarChart(graphObject, authParams, deepDive);
        dispatch(setOptionHighChart(updateOptions));
      } else if (
        //stack bar
        (type === "horizontal-bar" || type === "bar" || type === "lollipop") &&
        stacked &&
        IsEmpty(updatedGraphData)
      ) {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};

        let graphObject = {
          ...basicGraphObject,
          single: single,
          xaxis: xaxis,
          yaxis: yaxis,
          style: style,
          stacked: stacked,
          chart3dOption: chart3dOption,
          chartShow3d: chartShow3d,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          percent: percent,
          chartGlobalDataLabel: chartGlobalDataLabel,
          show_horizontal_view:
            metadata?.chart?.chart_settings?.show_horizontal_view,
          chartAxisLabel: chartAxisLabel,
          chartDataLabelInPercent: chartDataLabelInPercent,
          chartGradientColor: chartGradientColor,
          chartGlobalDataStacked: chartGlobalDataStacked,
          yAxisScale: yAxisScale,
          // aiLogScale: aiLogScale !== null ? aiLogScale : log,
          // All_WM_attributes: All_WM_attributes,
          // pdfDownloadStatus: PdfDownloadStatus
          //   ? PdfDownloadStatus
          //   : isPPTModalOpen,
          // competitorBenchmark:
          //   dashboardCompetitorList ||
          //   widgetMakerCompetitorList ||
          //   templateDetailsCompetitorList ||
          //   pdfCompititorList,
          // panes: panes,
          // index: index,
          // dashboard: dashboard,
          // ticket_widget_id: ticket_widget_id,
          // widgetGraphConfig: widgetGraphConfig,
          // widget_name: metadata?.widget_name,
          // metadata: metadata,
          // ticket_chart_type: ticket_chart_type,
          // section_index: section_index,
          // widget_index: widget_index,
          // section: Section,
          // template_widget: template_widget,
          // shareTemplateData: shareTemplateData,
          // graphConditionConfig: graphConditionConfig,
          // ticket_status_duration: ticket_status_duration,
          // isScheduleReportOpen: isScheduleReportOpen,
          template_details: templateDetails,
        };

        updateOptions[id] = getStackBarChart(graphObject, authParams);

        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "pie" || type === "donut") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          single: single,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          style: style,
          chart3dOption: chart3dOption,
          chartShow3d: chartShow3d,
          xaxis: xaxis,
          yaxis: yaxis,
          pdfDownloadStatus: PdfDownloadStatus
            ? PdfDownloadStatus
            : isPPTModalOpen,
          widget_name: metadata?.widget_name,
          metadata: metadata,
          isScheduleReportOpen: isScheduleReportOpen,
          accentColors: accentColors,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getPie(graphObject, authParams);

        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "multiple-pie") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          single: single,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          style: style,
          xaxis: xaxis,
          yaxis: yaxis,
          pdfDownloadStatus: PdfDownloadStatus
            ? PdfDownloadStatus
            : isPPTModalOpen,
          widget_name: metadata?.widget_name,
          accentColors: accentColors,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getMultiplePie(graphObject, authParams);

        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "line" && IsEmpty(updatedGraphData)) {
        if (single[0]?.data) {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};

          let graphObject = {
            ...basicGraphObject,
            single: single,
            title: title,
            colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartAxisLabel: chartAxisLabel,
            chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            // widgetGraphConfig: widgetGraphConfig,
            // metadata: metadata,
            // aiLogScale: aiLogScale !== null ? aiLogScale : log,
            // All_WM_attributes: All_WM_attributes,
            // pdfDownloadStatus: PdfDownloadStatus
            //   ? PdfDownloadStatus
            //   : isPPTModalOpen,
            // competitorBenchmark:
            //   dashboardCompetitorList ||
            //   widgetMakerCompetitorList ||
            //   templateDetailsCompetitorList ||
            //   pdfCompititorList,
            // panes: panes,
            // index: index,
            // dashboard: dashboard,
            // widget_name: metadata?.widget_name,
            // ticket_chart_type: ticket_chart_type,
            // section_index: section_index,
            // widget_index: widget_index,
            // section: Section,
            // template_widget: template_widget,
            // shareTemplateData: shareTemplateData,
            // graphConditionConfig: graphConditionConfig,
            // ticket_status_duration: ticket_status_duration,
            // isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            screen: screen,
            isDeepDive: screen?.isDeepDive,
            dateAggregationOfThisGraph: dateAggregationOfThisGraph,
            deepdiveObject: deepdive_object,
          };

          updateOptions[id] = getMultiLine(graphObject, authParams, Highcharts);
          dispatch(setOptionHighChart(updateOptions));
        } else {
          let updateOptions = global.options
            ? { ...global?.options }
            : { ...opData };
          let graphObject = {
            ...basicGraphObject,
            single: single,
            title: title,
            colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: "line",
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartAxisLabel: chartAxisLabel,
            chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            // widgetGraphConfig: widgetGraphConfig,
            // metadata: metadata,
            // aiLogScale: aiLogScale !== null ? aiLogScale : log,
            // All_WM_attributes: All_WM_attributes,
            // pdfDownloadStatus: PdfDownloadStatus
            //   ? PdfDownloadStatus
            //   : isPPTModalOpen,
            // competitorBenchmark:
            //   dashboardCompetitorList ||
            //   widgetMakerCompetitorList ||
            //   templateDetailsCompetitorList ||
            //   pdfCompititorList,
            // widget_name: metadata?.widget_name,
            // section_index: section_index,
            // widget_index: widget_index,
            // panes: panes,
            // index: index,
            // dashboard: dashboard,
            // section: Section,
            // template_widget: template_widget,
            // shareTemplateData: shareTemplateData,
            // graphConditionConfig: graphConditionConfig,
            // isScheduleReportOpen: isScheduleReportOpen,
            screen: screen,
            template_details: templateDetails,
          };

          updateOptions[id] = getLine(graphObject, authParams);
          dispatch(setOptionHighChart(updateOptions));
        }
      } else if (type === "area" && IsEmpty(updatedGraphData)) {
        if (single && single[0]?.data) {
          let updateOptions = global.options
            ? { ...global?.options }
            : opData
            ? { ...opData }
            : {};
          let graphObject = {
            ...basicGraphObject,
            single: single,
            title: title,
            colors: colors,
            xaxis: xaxis,
            yaxis: yaxis,
            type: type,
            chart3dOption: chart3dOption,
            chartShow3d: chartShow3d,
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartAxisLabel: chartAxisLabel,
            //chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            //xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            chartGlobalDataStacked: chartGlobalDataStacked,
            // widgetGraphConfig: widgetGraphConfig,
            // metadata: metadata,
            // aiLogScale: aiLogScale !== null ? aiLogScale : log,
            // All_WM_attributes: All_WM_attributes,
            // pdfDownloadStatus: PdfDownloadStatus
            //   ? PdfDownloadStatus
            //   : isPPTModalOpen,
            // competitorBenchmark:
            //   dashboardCompetitorList ||
            //   widgetMakerCompetitorList ||
            //   templateDetailsCompetitorList ||
            //   pdfCompititorList,
            // widget_name: metadata?.widget_name,
            // section_index: section_index,
            // widget_index: widget_index,
            // panes: panes,
            // index: index,
            // dashboard: dashboard,
            // section: Section,
            // template_widget: template_widget,
            // shareTemplateData: shareTemplateData,
            // graphConditionConfig: graphConditionConfig,
            // isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
          };

          updateOptions[id] = getMultiArea(graphObject, authParams);
          dispatch(setOptionHighChart(updateOptions));
        } else {
          let updateOptions = global.options
            ? { ...global?.options }
            : { ...opData };
          let graphObject = {
            ...basicGraphObject,
            single: single,
            title: title,
            colors: colors,
            chart3dOption: chart3dOption,
            chartShow3d: chartShow3d,
            xaxis: xaxis,
            yaxis: yaxis,
            type: type,
            legend_position: legend_position,
            show_legend: show_legend,
            chartGlobalDataLabel: chartGlobalDataLabel,
            chartAxisLabel: chartAxisLabel,
            //chartXaxisDataLabel: chartXaxisDataLabel,
            chartMarkers: chartMarkers,
            //xAxisScale: xAxisScale,
            yAxisScale: yAxisScale,
            style: style,
            // widgetGraphConfig: widgetGraphConfig,
            // metadata: metadata,
            // aiLogScale: aiLogScale !== null ? aiLogScale : log,
            // All_WM_attributes: All_WM_attributes,
            // pdfDownloadStatus: PdfDownloadStatus
            //   ? PdfDownloadStatus
            //   : isPPTModalOpen,
            // competitorBenchmark:
            //   dashboardCompetitorList ||
            //   widgetMakerCompetitorList ||
            //   templateDetailsCompetitorList ||
            //   pdfCompititorList,
            // widget_name: metadata?.widget_name,
            // section_index: section_index,
            // widget_index: widget_index,
            // panes: panes,
            // index: index,
            // dashboard: dashboard,
            // section: Section,
            // template_widget: template_widget,
            // shareTemplateData: shareTemplateData,
            // graphConditionConfig: graphConditionConfig,
            // isScheduleReportOpen: isScheduleReportOpen,
            template_details: templateDetails,
            screen: screen,
            accentColors: accentColors,
          };

          updateOptions[id] = getArea(graphObject, authParams);
          dispatch(setOptionHighChart(updateOptions));
        }
      } else if (type === "sunburst") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          single: single,
          title: title,
          colors: colors,
          type: type,
          style: style,
          pdfDownloadStatus: PdfDownloadStatus ? PdfDownloadStatus : false,
          pptDownloadStatus: isPPTModalOpen ? isPPTModalOpen : false,
          widget_name: metadata?.widget_name,
          metadata: metadata,
          screen: screen,
          accentColors: accentColors,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getSunburst(graphObject, authParams);
        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "scatter") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          ...basicGraphObject,
          single: single,
          title: title,
          colors: colors,
          xaxis: xaxis,
          yaxis: yaxis,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          chartGlobalDataLabel: chartGlobalDataLabel,
          chartAxisLabel: chartAxisLabel,
          chartXaxisDataLabel: chartXaxisDataLabel,
          chartMarkers: chartMarkers,
          showAsMedian: showAsMedian,
          xAxisScale: xAxisScale,
          yAxisScale: yAxisScale,
          style: style,
          screen: screen,
          template_details: templateDetails,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };
        updateOptions[id] = getScatter(graphObject, authParams);
        dispatch(setOptionHighChart(updateOptions));

        // updateOptions[id] = getScatter(
        // 	single,
        // 	title,
        // 	colors,
        // 	xaxis,
        // 	yaxis,
        // 	type,
        // 	legend_position
        // );
      } else if (type === "quadrant") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          ...basicGraphObject,
          single: single,
          title: title,
          colors: colors,
          xaxis: xaxis,
          yaxis: yaxis,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          chartGlobalDataLabel: chartGlobalDataLabel,
          chartAxisLabel: chartAxisLabel,
          chartXaxisDataLabel: chartXaxisDataLabel,
          chartMarkers: chartMarkers,
          showAsMedian: showAsMedian,
          xAxisScale: xAxisScale,
          yAxisScale: yAxisScale,
          style: style,
          screen: screen,
          template_details: templateDetails,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getQuadrant(graphObject, authParams);
        dispatch(setOptionHighChart(updateOptions));

        // updateOptions[id] = getQuadrant(
        // 	single,
        // 	title,
        // 	colors,
        // 	xaxis,
        // 	yaxis,
        // 	type,
        // 	legend_position
        // );
      } else if (type === "combination") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let obj = {
          ...basicGraphObject,
          single: single,
          title: title,
          colors: colors,
          xaxis: xaxis,
          yaxis: yaxis,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          xAxisChartType: xAxisChartType,
          metadata: metadata,
          y_series2: y_series2,
          chartGlobalDataLabel: chartGlobalDataLabel,
          chartAxisLabel: chartAxisLabel,
          yAxisScale: yAxisScale,
          attribute: attribute,
          yAxisMode: yAxisMode,
          chartMarkers: chartMarkers,
          style: style,
          // widgetGraphConfig: widgetGraphConfig,
          // All_WM_attributes: All_WM_attributes,
          // aiLogScale: aiLogScale !== null ? aiLogScale : log,
          // pdfDownloadStatus: PdfDownloadStatus
          //   ? PdfDownloadStatus
          //   : isPPTModalOpen,
          // competitorBenchmark:
          //   dashboardCompetitorList ||
          //   widgetMakerCompetitorList ||
          //   templateDetailsCompetitorList ||
          //   pdfCompititorList,
          // widget_name: metadata?.widget_name,
          chartGradientColor: chartGradientColor,
          chartGlobalDataStacked: chartGlobalDataStacked,
          // section_index: section_index,
          // widget_index: widget_index,
          // panes: panes,
          // index: index,
          // dashboard: dashboard,
          // section: Section,
          // template_widget: template_widget,
          // shareTemplateData: shareTemplateData,
          // graphConditionConfig: graphConditionConfig,
          // trends_type: trends_type,
          // template_id: template_id,
          // isScheduleReportOpen: isScheduleReportOpen,
          template_details: templateDetails,
          screen: screen,
          isDeepDive: screen?.isDeepDive,
          dateAggregationOfThisGraph: dateAggregationOfThisGraph,
          deepdiveObject: deepdive_object,
        };

        updateOptions[id] = getCombination(obj, authParams);
        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "heat" || type === "heatmap") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          ...basicGraphObject,
          single: single,
          title: title,
          colors: colors,
          type: type,
          xaxis: xaxis,
          yaxis: yaxis,
          split: split,
          style: style,
          widgetGraphConfig: widgetGraphConfig,
          metadata: metadata,
          pdfDownloadStatus: PdfDownloadStatus
            ? PdfDownloadStatus
            : isPPTModalOpen,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
          screen: screen,
          template_details: templateDetails,
        };

        updateOptions[id] = getHeat(graphObject, authParams);
        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "wordcloud") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          single: single,
          title: title,
          colors: colors,
          type: type,
          style: style,
          xaxis: xaxis,
          yaxis: yaxis,
          differential: differential,
          pdfDownloadStatus: PdfDownloadStatus
            ? PdfDownloadStatus
            : isPPTModalOpen,
          widget_name: metadata?.widget_name,
          widgetGraphConfig: widgetGraphConfig,
          metadata: metadata,
          fontFamily: fontFamily,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getWordcloud(graphObject, Highcharts, authParams);
        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "organization") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          single: single,
          title: title,
          colors: colors,
          type: type,
          style: style,
          xaxis: xaxis,
          yaxis: yaxis,
          differential: differential,
          pdfDownloadStatus: PdfDownloadStatus
            ? PdfDownloadStatus
            : isPPTModalOpen,
          widget_name: metadata?.widget_name,
          widgetGraphConfig: widgetGraphConfig,
          metadata: metadata,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getOrganization(graphObject, Highcharts);
        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "sankey") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          single: single,
          title: title,
          colors: colors,
          type: type,
          style: style,
          pdfDownloadStatus: PdfDownloadStatus ? PdfDownloadStatus : false,
          pptDownloadStatus: isPPTModalOpen ? isPPTModalOpen : false,
          widget_name: metadata?.widget_name,
          metadata: metadata,
          screen: screen,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };

        updateOptions[id] = getSankey(graphObject, authParams);
        dispatch(setOptionHighChart(updateOptions));
      } else if (type === "treemap") {
        let updateOptions = global.options
          ? { ...global?.options }
          : opData
          ? { ...opData }
          : {};
        let graphObject = {
          ...basicGraphObject,
          single: single,
          title: title,
          colors: colors,
          xaxis: xaxis,
          yaxis: yaxis,
          type: type,
          legend_position: legend_position,
          show_legend: show_legend,
          chartGlobalDataLabel: chartGlobalDataLabel,
          chartAxisLabel: chartAxisLabel,
          chartXaxisDataLabel: chartXaxisDataLabel,
          chartMarkers: chartMarkers,
          showAsMedian: showAsMedian,
          xAxisScale: xAxisScale,
          yAxisScale: yAxisScale,
          style: style,
          screen: screen,
          template_details: templateDetails,
          isDeepDiveView: isDeepDiveView,
          isDeepDiveDashboardView: isDeepDiveDashboardView,
          deepdiveDashboardData: deepdiveDashboardData,
          deepdiveDashboardTemplateData: deepdiveDashboardTemplateData,
        };
        updateOptions[id] = getTreeMap(graphObject, authParams);
        dispatch(setOptionHighChart(updateOptions));
      }
    }
    // && ((opData && !opData[id]) || !opData)
    //to control multiple rendering
    // if (opData && opData[id]) {
    //   opData[id].count = opData[id]?.count >= 0 ? opData[id]?.count + 1 : 0;
    // }
    const timer = setTimeout(
      () => {
        if (currentSelectedGraph && opData) {
          Object.keys(currentSelectedGraph).length > 0 &&
            currentSelectedGraph?.reflow();
          //currentSelectedGraph?.redraw();
          // currentSelectedGraph?.reflow(false);
          // currentSelectedGraph?.reflow(true);
          //Object.keys(currentSelectedGraph).length > 0 && currentSelectedGraph?.redraw();
        }
      },
      [isshowSideBar],
      500
    );
    return () => {
      clearTimeout(timer);
    };
  }, [
    type,
    stacked,
    id,
    legend_position,
    chartGlobalDataLabel,
    chartShowDetailsOnCard,
    chartGradientColor,
    chart3dOption,
    chartShow3d,
    show_horizontal_view,
    chartAxisLabel,
    chartDataLabelInPercent,
    chartGlobalDataStacked,
    chartXaxisDataLabel,
    chartMarkers,
    chartStyles,
    showAsMedian,
    xAxisChartType,
    yAxisChartType,
    xAxisScale,
    yAxisScale,
    widgetGraphConfig?.y_axis_settings,
    widgetGraphConfig,
    isshowSideBar,
    yAxisMode,
    style,
    Section,
    panes,
    aiLogScale,
    isPPTModalOpen,
    authParams,
    shareTemplateData,
    screen,
    screenRefreshEvent,
    personalizationSettingsObj?.togglePersonalisation,
  ]);
  const onScroll = (e) => {
    setxPosition(0);
    setyPosition(0);
    if (refTooltip.current && !refTooltip.current.contains(e.target)) {
      setxPosition(0);
      setyPosition(0);
    } else {
      setxPosition(0);
      setyPosition(0);
    }
  };
  useEffect(() => {
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  useEffect(() => {
    if (currentSelectedGraph && opData?.[id]) {
      currentSelectedGraph?.reflow();
      //currentSelectedGraph?.redraw();
    }
  }, [isWidgetEdit, span_data?.span, isPPTModalOpen]);

  const [sectionMetaData, widgetMetadData] = useMemo(() => {
    let shareSections = shareTemplateData?.section
      ? shareTemplateData?.section?.[0]
      : shareTemplateData?.sections[section_index];

    let widget_metadata =
      pathName.toLowerCase() === "commandcenter"
        ? section_index !== null &&
          section_index !== undefined &&
          screen?.sections &&
          screen?.sections?.[section_index]?.widgets?.[widget_index]
          ? screen?.sections?.[section_index]?.widgets?.[widget_index]
          : section_index >= 0 &&
            screen?.sections &&
            screen?.sections?.[section_index]
          ? screen?.sections?.[section_index]
          : screen?.section?.[0]?.widgets?.[widget_index]
          ? screen?.section?.[0]?.widgets?.[widget_index]
          : screen?.widgets?.[widget_index]
        : shareBrands
        ? shareSections?.widgets?.[widget_index]
        : section_index >= 0
        ? panes[index]?.sections?.[section_index]?.widgets?.[widget_index]
        : panes[index]?.section?.[0]?.widgets?.[widget_index];

    let section_metadata =
      pathName?.toLowerCase() === "commandcenter"
        ? section_index !== null &&
          section_index !== undefined &&
          screen?.sections?.[section_index]
          ? screen?.sections?.[section_index]
          : section_index >= 0 && screen?.sections?.[section_index]
          ? screen?.sections?.[section_index]
          : screen?.section?.[0]
        : shareBrands
        ? shareSections
        : section_index >= 0
        ? panes?.[index]?.sections?.[section_index]
        : panes?.[index]?.section?.[0];

    return [section_metadata, widget_metadata];
  }, [pathName, shareTemplateData, shareBrands, index]);

  const thisCurrentGraph = (chart) => {
    setCurrentSelectedGraph(chart);
  };
  const copyData = async (data) => {
    const copyText = document.getElementsByClassName("resize__chart--window");
    //copyText.select();
    const codata = await data?.series[0]?.data?.map((el) => el?.name);
    await navigator.clipboard.writeText(codata && codata);
  };
  // function to get node data for Sankey Deepdive
  const getSankeyNode = (ID, nodes) => {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === ID) {
        return nodes[i];
      }
    }
    return null;
  };
  // this function puts value in deepdive payload based on key for journey at
  const getConfigFilters = (cordinates, widget_metadata, key) => {
    // cordinates?.from === nodes?.[index]?.id ---> nodes?.[index]?.attribute
    // var = nodes?.[index]?.attribute
    // widget_metadata?.x_axis?.config_filters?.[index]?.attribute || widgetmetadata?.y_axes?.[index]?.config_filters?.[0]?.attribute === var ----> config_filter

    // this code is to form combined_config_filters: which is all the config_filters in all the axes in a single array
    let x_axis_in_widgetMetadata = deepCopy(widget_metadata?.x_axis);
    let y_axis_in_widgetMetadata = deepCopy(widget_metadata?.y_axes);
    let AxisData = [];
    if (
      y_axis_in_widgetMetadata &&
      !y_axis_in_widgetMetadata?.includes(x_axis_in_widgetMetadata)
    ) {
      y_axis_in_widgetMetadata.push(x_axis_in_widgetMetadata);
    } else if (!y_axis_in_widgetMetadata) {
      y_axis_in_widgetMetadata = [x_axis_in_widgetMetadata];
    }
    AxisData = [...y_axis_in_widgetMetadata];

    if (key === "combineConfigFilter") {
      let allConfigFilters = [];
      AxisData.forEach((item) => {
        if (item?.config_filters && Array.isArray(item?.config_filters)) {
          allConfigFilters = allConfigFilters?.concat(item?.config_filters);
        }
      });
      return allConfigFilters;
    } else {
      let dataInNodes = getSankeyNode(cordinates, single?.nodes);
      let attributeInNodes = dataInNodes?.attribute;
      for (let i = 0; i < AxisData?.length; i++) {
        // looping in payload based on attributeInNodes
        if (AxisData?.[i]?.attribute === attributeInNodes) {
          // AxisData?.[i]?.attribute === attributeInNodes will be true only for attributes without config_filters
          if (key === "config_filters") {
            // config_filters will be null for non-journey attributes
            return null;
          } else if (key === "attribute") {
            return AxisData?.[i]?.attribute;
          } else if (key === "trace_name") {
            return AxisData?.[i]?.trace_name;
          }
        }
        for (let j = 0; j < AxisData?.[i]?.config_filters?.length; j++) {
          // deeper looping nests for journey attributes
          if (
            AxisData?.[i]?.config_filters?.[j]?.attribute === attributeInNodes
          ) {
            if (key === "config_filters") {
              for (
                let z = 0;
                z < AxisData?.[i]?.config_filters?.[j]?.columns?.length;
                z++
              ) {
                if (
                  AxisData?.[i]?.config_filters?.[j]?.columns?.[z]?.name ===
                  dataInNodes?.display_name
                ) {
                  let newColumns = [
                    AxisData?.[i]?.config_filters?.[j]?.columns?.[z],
                  ];
                  let configFilters = AxisData?.[i]?.config_filters?.[j];
                  configFilters.columns = newColumns;
                  return [configFilters];
                }
              }
            } else if (key === "attribute") {
              return AxisData?.[i]?.attribute;
            } else if (key === "trace_name") {
              return AxisData?.[i]?.trace_name;
            }
          }
        }
      }
    }
  };

  const handleOnClickDeepDive = (
    cordinates,
    section_index,
    widget_index,
    single
  ) => {
    // adding restriction for deepdive in personalize preview window
    if (
      (!authParams?.screen_id &&
        !shareBrands &&
        graphConditionConfig?.template_calender_action_controls?.includes(
          panes[index]?.template_id
        )) ||
      (shareBrands &&
        graphConditionConfig?.template_calender_action_controls?.includes(
          shareTemplateData?.template_id
        ))
    ) {
      callNotification("Deep dive on this dashboard is not available", "error");
      return;
    }
    if (personalizationSettingsObj?.isPersonalizationPreviewOpen) {
      callNotification(
        "Actions are not allowed while customizing",
        "info",
        40,
        "personalization-preview-notification personalization-preview-mask",
        true
      );
      return;
    }
    // now deepdive on category screen widget functionality changed that's why adding false with this if block
    if (metadata?.widget_id === "000-100-956" && false) {
      if (metadata) {
        let updatedAttribute =
          cordinates?.parent === "" || cordinates?.parent === "0.0"
            ? "Category"
            : Number(cordinates?.parent) > 0.0
            ? "SubCategory"
            : "SubSubCategory";

        // console.log("updatedAttribute", updatedAttribute);
        // let globalFilters = screen?.filters?.length ? screen?.filters : [];
        let categoryFilters = screen?.category_screen_filters?.length
          ? screen?.category_screen_filters
          : [];
        let updatedFilters = [];
        if (
          cordinates?.parent === "" &&
          screen?.category_screen_filters?.length
        ) {
          if (categoryFilters?.length) {
            updatedFilters = categoryFilters?.filter(
              (d) =>
                d?.attribute !== "Category" &&
                d?.attribute !== "SubCategory" &&
                d?.attribute !== "SubSubCategory" &&
                d?.attribute !== "UserLocation"
            );
          }
          dispatch(setCategoryScreenFilters(updatedFilters));
          dispatch(
            setClearScreenWidgetGraphData(
              section_index,
              widget_index,
              "NOT_DELETE_SELECTED_WIDGET_ID",
              "1"
            )
          );
        } else if (cordinates?.parent !== "") {
          if (categoryFilters?.length) {
            categoryFilters = categoryFilters?.filter(
              (d) =>
                d?.attribute !== "Category" &&
                d?.attribute !== "SubCategory" &&
                d?.attribute !== "SubSubCategory"
            );
          }
          let appliedFilter =
            updatedAttribute === "SubCategory"
              ? [
                  {
                    chart_value: cordinates?.value,
                    source_widget_id: metadata?.widget_id,
                    attribute: "Category",
                    type: "varchar",
                    columns: [
                      {
                        name: cordinates?.node?.parentNode?.name,
                        type: "include",
                      },
                    ],
                  },
                  {
                    chart_value: cordinates?.value,
                    source_widget_id: metadata?.widget_id,
                    attribute: updatedAttribute,
                    type: "varchar",
                    columns: [
                      {
                        name: cordinates?.name,
                        type: "include",
                      },
                    ],
                  },
                ]
              : [
                  {
                    chart_value: cordinates?.value,
                    source_widget_id: metadata?.widget_id,
                    attribute: updatedAttribute,
                    type: "varchar",
                    columns: [
                      {
                        name: cordinates?.name,
                        type: "include",
                      },
                    ],
                  },
                ];
          updatedFilters = [...categoryFilters, ...appliedFilter];

          dispatch(setCategoryScreenFilters(updatedFilters));
          dispatch(
            setClearScreenWidgetGraphData(
              section_index,
              widget_index,
              "NOT_DELETE_SELECTED_WIDGET_ID",
              "1"
            )
          );
        }
      }
    } else {
      let shareSections = shareTemplateData?.section
        ? shareTemplateData?.section?.[0]
        : shareTemplateData?.sections?.[section_index];

      let checkAttType = [];
      WM_attributes?.forEach((e) => {
        return e[1]?.forEach((el) => {
          if (el[1]?.datatype === "int") {
            checkAttType?.push(el[1]?.title);
          }
        });
      });
      let pathName = getWindowLocationPath()?.toLowerCase();
      let seriesNamesForAggregation =
        benchmarkAggregation && Array.isArray(benchmarkAggregation)
          ? benchmarkAggregation?.map(
              (aggrigation, i) => `Group's ${aggrigation?.label}`
            )
          : [];
      if (seriesNamesForAggregation) {
        seriesNamesForAggregation = [
          ...seriesNamesForAggregation,
          "Benchmark",
          "Custom Benchmark",
        ];
      }
      let deepDiveOnMultiLevel =
        type === "treemap"
          ? cordinates?.options?.parent !== "" && cordinates?.node?.isLeaf // in treemap, we are checking if the node is leaf to allow deepdive
          : cordinates?.options?.parent !== "" && cordinates?.node?.level !== 2;
      let isAggregationSeries = seriesNamesForAggregation?.includes(
        cordinates?.series?.name
      );
      if (
        (isDeepDiveDashboardView &&
          deepDiveOnMultiLevel &&
          !isAggregationSeries) ||
        (!isDeepDiveDashboardView &&
          panes[getPaneIndex(panes, activeKey)]?.isDashboard &&
          deepDiveOnMultiLevel &&
          !isAggregationSeries) ||
        (!isDeepDiveDashboardView &&
          shareBrands &&
          deepDiveOnMultiLevel &&
          !isAggregationSeries) ||
        (!isDeepDiveDashboardView &&
          pathName === "CommandCenter"?.toLowerCase() &&
          deepDiveOnMultiLevel &&
          !isAggregationSeries)
      ) {
        let dashboardDataOfDeepDive = deepdiveDashboardData;
        let deepdiveTemplatData = deepdiveDashboardTemplateData;
        let sectionData = isDeepDiveDashboardView
          ? deepdiveTemplatData?.sections?.[section_index]
          : null;
        let widget_metadata = isDeepDiveDashboardView
          ? sectionData?.widgets?.[widget_index]
          : pathName === "CommandCenter"?.toLowerCase()
          ? section_index !== null &&
            section_index !== undefined &&
            screen?.sections &&
            screen?.sections?.[section_index]?.widgets?.[widget_index]
            ? screen?.sections?.[section_index]?.widgets?.[widget_index]
            : section_index >= 0 &&
              screen?.sections &&
              screen?.sections?.[section_index]
            ? screen?.sections?.[section_index]
            : screen?.section?.[0]?.widgets?.[widget_index]
            ? screen?.section?.[0]?.widgets?.[widget_index]
            : screen?.widgets && screen?.widgets?.[widget_index]
            ? screen?.widgets && screen?.widgets?.[widget_index]
            : null
          : shareBrands
          ? shareSections?.widgets?.[widget_index]
          : section_index >= 0
          ? panes?.[index]?.sections?.[section_index]?.widgets?.[widget_index]
          : panes?.[index]?.section?.[0]?.widgets?.[widget_index];

        // Checking if `widget_metadata` exists — if so, use it; otherwise, fallback to `metadata`.
        // Slight confusion: if other values are being calculated based on `widget_metadata`,
        // why is `metadata` being used directly in some cases?
        // To ensure consistency, always compare both `widget_metadata` and `metadata` before proceeding.

        widget_metadata = widget_metadata
          ? widget_metadata
          : metadata
          ? metadata
          : null;
        let widget_ids =
          widget_metadata?.data_source === "mentions" &&
          (graphConditionConfig?.["workflow-attrs"]?.includes(
            widget_metadata?.x_axis?.attribute
          ) ||
            graphConditionConfig?.["workflow-attrs"]?.includes(
              widget_metadata?.y_series?.[0]?.attribute
            ))
            ? ["8.1"]
            : widget_metadata?.ticket_deep_dive &&
              widget_metadata?.data_source === "ticket"
            ? ["9.1"]
            : widget_metadata?.data_source === "tickets"
            ? ["6.1"]
            : widget_metadata?.deep_dive_id
            ? widget_metadata?.deep_dive_id
            : workspace_privileges?.environment === 2 ||
              share_workspace_privileges?.environment === 2
            ? ["info-bar", "2.1", "2.2", "2.3", "2.4"]
            : ["info-bar", "1.1", "1.2"];

        let duration =
          pathName === "CommandCenter"?.toLowerCase()
            ? screen?.duration
            : !shareBrands && panes[index]?.duration; //global date
        // Deep Dive Enable
        let widgetDataSource = widget_metadata?.data_source
          ? widget_metadata?.data_source
          : "mentions";

        if (
          // This condition commenting out as it is checking now based on attributes, which is hanldling in inner if block
          // !graphConditionConfig?.[`default_attrs`]?.page
          //   ?.map((d) => d?.attribute)
          //   ?.includes(widget_metadata?.y_series?.[0]?.attribute) &&
          widget_metadata?.chart?.chart_settings?.allow_deepdive !== false &&
          widget_metadata?.x_axis?.attribute !== "Plan_Delivered" &&
          (widget_metadata?.type !== "predefined" ||
            (widget_metadata?.type === "predefined" &&
              widget_metadata?.ticket_deep_dive) ||
            (widget_metadata?.deep_dive_id === "ai-actionable" &&
              cordinates?.options?.widget_inner_id &&
              cordinates?.options?.value > 0) ||
            (authParams?.screen_id &&
              widget_metadata?.chart?.chart_settings?.allow_deepdive)) &&
          ((widget_metadata?.chart?.chart_type === "sankey" &&
            cordinates?.from &&
            cordinates?.to) ||
            widget_metadata?.chart?.chart_type !== "sankey") &&
          !graphConditionConfig?.common
            ?.map((d) => d?.attribute)
            ?.includes(widget_metadata?.y_series?.[0]?.attribute)
        ) {
          let index =
            !shareBrands &&
            pathName !== "CommandCenter"?.toLowerCase() &&
            getPaneIndex(panes, activeKey);

          let section_metadata = isDeepDiveDashboardView
            ? sectionData
            : pathName === "CommandCenter"?.toLowerCase()
            ? section_index !== null &&
              section_index !== undefined &&
              screen?.sections &&
              screen?.sections?.[section_index]
              ? screen?.sections?.[section_index]
              : section_index >= 0 &&
                screen?.sections &&
                screen?.sections?.[section_index]
              ? screen?.sections?.[section_index]
              : screen?.section && screen?.section?.[0]
              ? screen?.section?.[0]
              : null
            : shareBrands
            ? shareSections
            : section_index >= 0
            ? panes[index]?.sections?.[section_index]
            : panes[index]?.section?.[0];
          let Section_is_locked = section_metadata?.is_locked;
          let widgets_is_locked = widget_metadata?.is_locked;
          let twoMonthsDuration = getCustomizedDate(6, "Days");
          let defaultDuration = {
            from:
              moment(twoMonthsDuration?.date[0]).format("YYYY/MM/DD") +
              " " +
              twoMonthsDuration?.time?.startTime,
            to:
              moment(twoMonthsDuration?.date[1]).format("YYYY/MM/DD") +
              " " +
              twoMonthsDuration?.time?.endTime,
          };

          let global_duration = isDeepDiveDashboardView
            ? dashboardDataOfDeepDive?.start_date
              ? {
                  from: dashboardDataOfDeepDive?.start_date,
                  to: dashboardDataOfDeepDive?.end_date,
                }
              : null
            : pathName === "CommandCenter"?.toLowerCase()
            ? screen?.duration
            : shareBrands
            ? sharedDuration?.from
              ? {
                  from: sharedDuration?.from,
                  to: sharedDuration?.to,
                }
              : null
            : panes[index]?.duration;

          // let global_brands = shareBrands
          //   ? shareBrands
          //   : getGlobalBrands(
          //       panes[index]?.brands,
          //       global_dashboard_single_brands,
          //       global_dashboard_brands,
          //       panes[index]?.template_type
          //     );
          let global_brands = isDeepDiveDashboardView
            ? dashboardDataOfDeepDive?.brands
            : pathName === "CommandCenter"?.toLowerCase()
            ? screen?.brands
            : shareBrands
            ? shareBrands
            : panes?.[index]?.brands;
          let brandIds =
            shareBrands ||
            pathName === "CommandCenter"?.toLowerCase() ||
            isDeepDiveDashboardView
              ? widget_metadata?.brand_ids
              : section_index >= 0
              ? panes[index]?.sections?.[section_index]?.widgets &&
                panes[index]?.sections?.[section_index]?.widgets[widget_index]
                  ?.brand_ids
              : panes[index]?.section?.[0]?.widgets[widget_index]?.brand_ids
              ? panes[index]?.section?.[0]?.widgets[widget_index]?.brand_ids
              : null;

          //share link filter code
          let sectionDateFilter =
            shareTemplateData?.sections && !isDeepDiveDashboardView
              ? {
                  from: shareTemplateData?.sections[section_index]?.start_date
                    ? shareTemplateData?.sections[section_index]?.start_date
                    : null,
                  to: shareTemplateData?.sections[section_index]?.end_date
                    ? shareTemplateData?.sections[section_index]?.end_date
                    : null,
                }
              : null;

          let widgetFilterDate = {
            from: widget_metadata?.start_date
              ? widget_metadata?.start_date
              : null,
            to: widget_metadata?.end_date ? widget_metadata?.end_date : null,
          };
          let combinedFilters = [];
          if (!widget_metadata?.is_locked) {
            if (widgetFilterDate?.from)
              combinedFilters.push({
                attribute: "CreatedDate",
                type: "datetime",
                columns: widgetFilterDate,
              });

            if (sectionDateFilter?.from && !section_metadata?.is_locked) {
              combinedFilters.push({
                attribute: "CreatedDate",
                type: "datetime",
                columns: sectionDateFilter,
              });
            }
            if (section_metadata?.brand_ids && !section_metadata?.is_locked) {
              let brandsForFilter = shareBrands
                ? getBrands(
                    shareBrands?.brand_groups,
                    section_metadata?.brand_ids,
                    shareBrands?.brand_list,
                    section_metadata?.brand_groups
                  )
                : getBrands(
                    brandlogicalgroup,
                    section_metadata?.brand_ids,
                    brands,
                    section_metadata?.brand_groups
                  );

              combinedFilters.push(brandsForFilter);
            }
            if (widget_metadata?.brand_ids) {
              let brandsForFilter = shareBrands
                ? getBrands(
                    shareBrands?.brand_groups,
                    widget_metadata?.brand_ids,
                    shareBrands?.brand_list,
                    widget_metadata?.brand_groups
                  )
                : getBrands(
                    brandlogicalgroup,
                    widget_metadata?.brand_ids,
                    brands,
                    widget_metadata?.brand_groups
                  );

              combinedFilters.push(brandsForFilter);
            }
          }

          if (widget_metadata?.filters)
            combinedFilters = [...widget_metadata?.filters, ...combinedFilters];
          if (section_metadata?.filters)
            combinedFilters = [
              ...section_metadata?.filters,
              ...combinedFilters,
            ];

          let filterData = combinedFilters?.filter((d) => d);
          let hidden_filter = shareTemplateData?.hidden_filter
            ? [shareTemplateData?.hidden_filter]
            : [];
          let quick_filter = shareTemplateData?.quick_filter
            ? [shareTemplateData?.quick_filter]
            : [];

          let widgetObj = {
            brand_groups: widget_metadata?.brand_groups,
            singleBrands: brands,
          };
          let brandsContainter = {
            brand_ids: widget_metadata?.brand_ids,
            brand_groups: widget_metadata?.brand_groups,
            singleBrands: shareBrands ? shareBrands?.brand_list : brands,
            brandGroupList: shareBrands
              ? shareBrands?.brand_groups
              : brandlogicalgroup,
          };

          let filter = isDeepDiveDashboardView
            ? getAllDeepDiveDashboardFilters(
                graphConditionConfig,
                deepdiveDashboardData,
                deepdiveDashboardTemplateData,
                section_index,
                widget_index,
                brandsContainter
              )
            : pathName === "CommandCenter"?.toLowerCase()
            ? getAllWidgetScreenFilters(
                screen,
                section_index,
                widget_index,
                brands,
                brandlogicalgroup,
                widget_metadata
              )
            : shareBrands
            ? getAllShareFilters(
                graphConditionConfig,
                shareTemplateData,
                section_index,
                widget_index,
                shareBrands
              )
            : getAllFilters(
                graphConditionConfig,
                panes,
                index,
                section_index,
                widget_index,
                brandlogicalgroup,
                brandIds,
                widgetObj
              );

          if (
            screen?.category_screen_filters?.length &&
            !isDeepDiveDashboardView
          ) {
            let category_screen_filters = screen?.category_screen_filters
              ?.length
              ? screen?.category_screen_filters
              : [];
            filter = [...filter, ...category_screen_filters];
          }

          let widget_brands = shareBrands
            ? getBrands(
                shareBrands?.brand_groups,
                widget_metadata?.brand_ids,
                shareBrands?.brand_list,
                widget_metadata?.brand_groups
              )
            : getBrands(
                brandlogicalgroup,
                widget_metadata?.brand_ids,
                brands,
                widget_metadata?.brand_groups
              );

          let section_brands = shareBrands
            ? getBrands(
                shareBrands?.brand_groups,
                section_metadata?.brand_ids,
                shareBrands?.brand_list,
                section_metadata?.brand_groups
              )
            : getBrands(
                brandlogicalgroup,
                section_metadata?.brand_ids,
                brands,
                section_metadata?.brand_groups
              );
          let defaultBrands = isDeepDiveDashboardView
            ? dashboardDataOfDeepDive?.brands
            : pathName === "CommandCenter"?.toLowerCase()
            ? brands
            : shareBrands
            ? shareBrands
            : null;

          let series_index = cordinates?.series?.index
            ? cordinates?.series?.index
            : 0;
          let isChartTypeChange = false;
          let validChartTypes = [
            "area",
            "bar",
            "horizontal-bar",
            "lollipop",
            "combination",
          ];
          if (
            validChartTypes?.includes(
              type ||
                widget_metadata?.chart?.chart_type ||
                widget_metadata?.graph_type
            ) &&
            ["line", "spline"].includes(opData?.[id]?.chart?.type) &&
            opData?.[id]?.isChartTypeChange
          ) {
            isChartTypeChange = true;
          }
          let isValidChartTypes = validChartTypes?.includes(
            type ||
              widget_metadata?.chart?.chart_type ||
              widget_metadata?.graph_type
          );
          let isStackedChartType =
            (isValidChartTypes && chartGlobalDataStacked) ||
            (isChartTypeChange &&
              isValidChartTypes &&
              (chartGlobalDataStacked || chartGlobalDataStacked == undefined));

          let single_data =
            Array.isArray(single) && single[0]?.data
              ? isStackedChartType &&
                Array.isArray(single[0]?.data) &&
                single[0]?.data?.length > 0
                ? single[0]?.data?.slice()?.reverse()
                : single[0]?.data
              : single?.y_axis
              ? opData[id]?.series
              : null;
          let ySeriesInWidgetMetadata = widget_metadata?.y_series
            ? widget_metadata?.y_series
            : [];
          if (Array.isArray(single) && single[0]?.data && isStackedChartType) {
            if (
              Array.isArray(ySeriesInWidgetMetadata) &&
              ySeriesInWidgetMetadata?.length > 0
            ) {
              ySeriesInWidgetMetadata = [...ySeriesInWidgetMetadata]
                ?.slice()
                ?.reverse();
            }
          }
          let y_axis_att =
            (type === "pie" || type === "treemap") &&
            Array.isArray(single) &&
            widget_metadata?.y_axes &&
            widget_metadata?.y_axes[0]?.attribute
              ? widget_metadata?.y_axes[0]?.attribute
              : type === "sunburst" ||
                type === "heatmap" ||
                widget_metadata?.primary
              ? widget_metadata?.primary
                ? widget_metadata?.primary
                : widget_metadata?.y_series?.[0]?.attribute
              : widget_metadata?.y_axes &&
                widget_metadata?.y_axes[0]?.attribute &&
                single_data
              ? single_data[series_index]?.attribute ===
                widget_metadata?.y_axes[0]?.attribute
                ? single_data[series_index]?.attribute
                : null
              : null;
          let y_series_att =
            // y_series_att for widget with journey attribute
            widget_metadata?.y_axes?.[0]?.config_filters?.length ||
            widget_metadata?.x_axis?.config_filters?.length
              ? type === "pie" &&
                Array.isArray(single) &&
                widget_metadata?.y_axes?.length > 0 &&
                widget_metadata?.y_axes?.[0]?.attribute
                ? widget_metadata?.y_series?.[0]?.attribute
                : type === "sunburst" ||
                  type === "heatmap" ||
                  type === "treemap"
                ? widget_metadata?.y_series?.[0]?.attribute
                : widget_metadata?.y_axes?.length > 0
                ? widget_metadata?.y_axes?.[0]?.attribute && single_data
                  ? single_data[series_index]?.attribute ===
                    widget_metadata?.y_axes?.[0]?.config_filters?.[0]?.attribute
                    ? widget_metadata?.y_series?.[0]?.attribute
                    : single_data[series_index]?.attribute
                  : null
                : single_data && single_data[series_index]?.attribute
              : // y_series_att for widgets without journey attribute
              type === "pie" &&
                Array.isArray(single) &&
                widget_metadata?.y_axes?.length > 0 &&
                widget_metadata?.y_axes?.[0]?.attribute
              ? widget_metadata?.y_series?.[0]?.attribute
              : type === "sunburst" || type === "heatmap" || type === "treemap"
              ? widget_metadata?.y_series?.[0]?.attribute
              : widget_metadata?.y_axes?.length > 0
              ? widget_metadata?.y_axes?.[0]?.attribute && single_data
                ? single_data[series_index]?.attribute ===
                  widget_metadata?.y_axes?.[0]?.attribute
                  ? widget_metadata?.y_series?.[0]?.attribute
                  : single_data[series_index]?.attribute
                : null
              : single_data && single_data[series_index]?.attribute;

          // Calculating trace name for getting correct series object, in case of breakdown
          let y_series_tracename =
            single_data?.[series_index]?.attribute ===
              widget_metadata?.y_axes?.[0]?.attribute ||
            type === "treemap" ||
            widget_metadata?.chart?.chart_type === "treemap" ||
            widget_metadata?.graph_type === "treemap"
              ? widget_metadata?.y_series?.[0]?.trace_name
                ? widget_metadata?.y_series?.[0]?.trace_name
                : allAttributes?.[
                    ySeriesInWidgetMetadata
                      ? widget_metadata?.y_axes?.length > 0 &&
                        widget_metadata?.chart?.chart_type !== "wordcloud"
                        ? y_series_att
                        : ySeriesInWidgetMetadata[series_index]?.attribute
                      : widget_metadata?.secondary
                  ]
              : single_data?.[series_index]?.parentName
              ? single_data?.[series_index]?.parentName
              : single_data?.[series_index]?.name;

          // Calculating data source for handling page attribute deepdive
          let y_series_datasource =
            // y_series_datasource for widget with journey attribute
            widget_metadata?.y_axes?.[0]?.config_filters?.length ||
            widget_metadata?.x_axis?.config_filters?.length
              ? type === "pie" &&
                Array.isArray(single) &&
                widget_metadata?.y_axes?.length > 0 &&
                widget_metadata?.y_axes?.[0]?.attribute
                ? widget_metadata?.y_series?.[0]?.data_source
                : type === "sunburst" ||
                  type === "heatmap" ||
                  type === "treemap"
                ? widget_metadata?.y_series?.[0]?.data_source
                : widget_metadata?.y_axes?.length > 0
                ? widget_metadata?.y_axes?.[0]?.attribute && single_data
                  ? single_data[series_index]?.attribute ===
                    widget_metadata?.y_axes?.[0]?.config_filters?.[0]?.attribute
                    ? widget_metadata?.y_series?.[0]?.data_source
                    : single_data[series_index]?.data_source
                  : null
                : single_data && single_data[series_index]?.data_source
              : // y_series_datasource for widgets without journey attribute
              type === "pie" &&
                Array.isArray(single) &&
                widget_metadata?.y_axes?.length > 0 &&
                widget_metadata?.y_axes?.[0]?.attribute
              ? widget_metadata?.y_series?.[0]?.data_source
              : type === "sunburst" || type === "heatmap" || type === "treemap"
              ? widget_metadata?.y_series?.[0]?.data_source
              : widget_metadata?.y_axes?.length > 0
              ? widget_metadata?.y_axes?.[0]?.attribute && single_data
                ? single_data[series_index]?.attribute ===
                  widget_metadata?.y_axes?.[0]?.attribute
                  ? widget_metadata?.y_series?.[0]?.data_source
                  : single_data[series_index]?.data_source
                : null
              : single_data && single_data[series_index]?.data_source;
          // If y_series_datasource is not present in the payload, then assigning it from widget_metadata
          // or defaulting to "mentions"
          if (!y_series_datasource) {
            y_series_datasource = widget_metadata?.data_source
              ? widget_metadata?.data_source
              : "mentions";
          }
          let y_series_filter =
            type === "sunburst" || type === "heatmap" || type === "treemap"
              ? widget_metadata?.y_series?.[0]?.filters
              : widget_metadata?.y_axes
              ? widget_metadata?.y_axes?.[0]?.attribute && single_data
                ? single_data[series_index]?.attribute ===
                  widget_metadata?.y_axes[0]?.attribute
                  ? single_data[series_index]?.filters
                  : single_data[series_index]?.filters
                : null
              : single_data && single_data[series_index]?.filters;

          //Now Calculating y_series object for deepdive_on payload
          let ySeriesObjForDeepDiveOn =
            widget_metadata?.chart?.chart_type === "scatter" ||
            widget_metadata?.graph_type === "scatter"
              ? {
                  filters: ySeriesInWidgetMetadata
                    ? ySeriesInWidgetMetadata[0]?.filters?.length > 0
                      ? ySeriesInWidgetMetadata[0]?.filters
                      : []
                    : [],
                  attribute: ySeriesInWidgetMetadata
                    ? ySeriesInWidgetMetadata[0]?.attribute
                      ? ySeriesInWidgetMetadata[0]?.attribute
                      : null
                    : null,
                  data_source: ySeriesInWidgetMetadata
                    ? ySeriesInWidgetMetadata[0]?.data_source
                      ? ySeriesInWidgetMetadata[0]?.data_source
                      : null
                    : null,
                  value: cordinates?.options?.x
                    ? cordinates?.options?.x
                    : cordinates?.x,
                  trace_name:
                    ySeriesInWidgetMetadata?.[0]?.trace_name ||
                    allAttributes?.[
                      ySeriesInWidgetMetadata
                        ? ySeriesInWidgetMetadata[0]?.attribute
                          ? ySeriesInWidgetMetadata[0]?.attribute
                          : ""
                        : ""
                    ]
                      ? ySeriesInWidgetMetadata?.[0]?.trace_name ||
                        allAttributes?.[
                          ySeriesInWidgetMetadata
                            ? ySeriesInWidgetMetadata[0]?.attribute
                              ? ySeriesInWidgetMetadata[0]?.attribute
                              : ""
                            : ""
                        ]
                      : "",
                }
              : widget_metadata?.chart?.chart_type === "survey-card"
              ? {
                  filters: [],
                  attribute: "tagid",
                  data_source: "mentions",
                  value:
                    type === "sunburst" || type === "sankey"
                      ? cordinates?.options?.value
                      : type === "wordcloud"
                      ? cordinates?.options?.weight
                      : type === "heatmap"
                      ? cordinates?.options?.value
                      : cordinates?.options?.y
                      ? cordinates?.options?.y
                      : cordinates?.options?.value,
                  trace_name: "Mention Count",
                }
              : !widget_metadata?.primary && type !== "sankey"
              ? {
                  filters: ySeriesInWidgetMetadata
                    ? widget_metadata?.y_axes?.length > 0
                      ? y_series_filter
                        ? y_series_filter
                        : []
                      : ySeriesInWidgetMetadata[series_index]?.filters
                    : [],
                  date_part: ySeriesInWidgetMetadata
                    ? ySeriesInWidgetMetadata[series_index]?.date_part &&
                      ySeriesInWidgetMetadata[series_index]?.date_part !== "D"
                      ? ySeriesInWidgetMetadata[series_index]?.date_part
                      : ySeriesInWidgetMetadata[series_index]?.date_aggregation
                      ? ySeriesInWidgetMetadata[series_index]?.date_aggregation
                      : null
                    : null,
                  special_cases: ySeriesInWidgetMetadata
                    ? ySeriesInWidgetMetadata[series_index]?.special_cases
                    : widget_metadata?.special_cases,
                  attribute: ySeriesInWidgetMetadata
                    ? widget_metadata?.y_axes?.length > 0 &&
                      widget_metadata?.chart?.chart_type !== "wordcloud"
                      ? y_series_att
                      : ySeriesInWidgetMetadata[series_index]?.attribute
                    : widget_metadata?.secondary,
                  data_source: ySeriesInWidgetMetadata
                    ? widget_metadata?.y_axes?.length > 0 &&
                      widget_metadata?.chart?.chart_type !== "wordcloud"
                      ? y_series_datasource
                      : ySeriesInWidgetMetadata[series_index]?.data_source
                    : widget_metadata?.data_source,
                  value:
                    widget_metadata?.chart?.chart_type === "pie" &&
                    widget_metadata?.y_axes?.length > 0
                      ? cordinates?.options?.y
                      : widget_metadata?.chart?.chart_type === "sunburst" ||
                        widget_metadata?.graph_type === "sunburst" ||
                        widget_metadata?.chart?.chart_type === "sankey" ||
                        widget_metadata?.graph_type === "sankey"
                      ? cordinates?.options?.value
                      : widget_metadata?.chart?.chart_type === "wordcloud" ||
                        widget_metadata?.graph_type === "wordcloud"
                      ? cordinates?.options?.weight
                      : widget_metadata?.chart?.chart_type === "heatmap" ||
                        widget_metadata?.graph_type === "heatmap" ||
                        widget_metadata?.chart?.chart_type === "treemap" ||
                        widget_metadata?.graph_type === "treemap"
                      ? cordinates?.options?.value
                      : cordinates?.options?.y,
                  trace_name: y_series_tracename
                    ? y_series_tracename
                    : allAttributes?.[
                        ySeriesInWidgetMetadata
                          ? widget_metadata?.y_axes?.length > 0 &&
                            widget_metadata?.chart?.chart_type !== "wordcloud"
                            ? y_series_att
                            : ySeriesInWidgetMetadata[series_index]?.attribute
                          : widget_metadata?.secondary
                      ]
                    ? allAttributes?.[
                        ySeriesInWidgetMetadata
                          ? widget_metadata?.y_axes?.length > 0 &&
                            widget_metadata?.chart?.chart_type !== "wordcloud"
                            ? y_series_att
                            : ySeriesInWidgetMetadata[series_index]?.attribute
                          : widget_metadata?.secondary
                      ]
                    : "",
                }
              : widget_metadata?.chart?.chart_type === "sankey"
              ? {
                  attribute: widget_metadata?.payload?.y_series?.[0]?.attribute,
                  data_source:
                    widget_metadata?.payload?.y_series?.[0]?.data_source,
                  trace_name: widget_metadata?.y_series?.[0]?.trace_name,
                  value:
                    cordinates?.formatPrefix === "point"
                      ? cordinates?.options?.weight
                      : cordinates?.sum,
                }
              : undefined;

          // Don't allow deepdive if y_series_datasource is page
          // if (y_series_datasource === "page") then show notification and return
          if (!ySeriesObjForDeepDiveOn?.data_source) {
            ySeriesObjForDeepDiveOn.data_source =
              widget_metadata?.data_source || "mentions";
          }
          if (ySeriesObjForDeepDiveOn.data_source === "page") {
            callNotification(
              "Deep dive on this attribute is not allowed",
              "info"
            );
            return;
          }

          dispatch(setDeepDiveFlag(true));

          !shareBrands &&
            pathName !== "CommandCenter"?.toLowerCase() &&
            dispatch(
              setSelectedDeepdiveWidget(
                section_index,
                widget_index,
                panes,
                index
              )
            );
          !shareBrands &&
            pathName !== "CommandCenter"?.toLowerCase() &&
            dispatch(setisDeepDive(true, panes, index));

          // To add selected widget of deep dive of share dashboard
          shareBrands &&
            dispatch(
              setShareSelectedDeepdiveWidget({
                section_index: section_index,
                widget_index: widget_index,
              })
            );

          // to add selected widget of deep dive in screen
          if (pathName === "CommandCenter"?.toLowerCase()) {
            dispatch(
              setSelectedDeepdiveWidgetInScreen(section_index, widget_index)
            );
          }

          // let updateShareTemplateData = shareTemplateData && {
          //   ...shareTemplateData,
          // };

          // if (updateShareTemplateData) {
          //   shareTemplateData.isDeepDive = true;
          //   updateShareTemplateData.isDeepDive = true;
          //   updateShareTemplateData.deep_dive = {
          //     infoBar: null,
          //     mentions: null,
          //     wordcloud: [],
          //     category: [],
          //     top_users: [],
          //     influencers: [],
          //     source_of_post: [],
          //     locations: [],
          //   };
          //   // dispatch({
          //   //   type: "SET_SHARE_TEMPLATE_DATA",
          //   //   payload: updateShareTemplateData,
          //   // });
          // }

          // This case is applied when we select competition brand and applied competition filter(Owned/Competition)
          // calculating brand in case of section comprtiotion brand type exists
          let sectionComprtitorBrandType =
            section_metadata?.comp_brand_type === false ||
            section_metadata?.comp_brand_type === true
              ? section_metadata?.comp_brand_type
              : null;
          let sectionComprtitorBrandTypeExists =
            sectionComprtitorBrandType === false ||
            sectionComprtitorBrandType === true;
          let globalBrandChangeInShare = false;
          let updatedComBrandList = [];
          if (
            !(Section_is_locked && section_brands?.columns?.length) &&
            sectionComprtitorBrandTypeExists
          ) {
            if (shareBrands) {
              let comBrand = defaultBrands?.brands?.columns?.[0]?.competitor
                ? defaultBrands?.brands?.columns?.[0]
                : defaultBrands?.brands?.[0]?.competitor
                ? defaultBrands?.brands?.[0]
                : defaultBrands?.brands?.competitor
                ? defaultBrands?.brands
                : null;
              let comBrandExists = comBrand?.competitor
                ? true
                : defaultBrands
                ? defaultBrands?.selected_brand_ids?.length &&
                  defaultBrands?.selected_comp_brand_ids?.length > 0
                : null;
              if (comBrandExists) {
                if (comBrand) {
                  if (sectionComprtitorBrandType === false) {
                    updatedComBrandList.push({
                      brand_id: comBrand?.name,
                      brand_name: comBrand?.brand_name,
                      brand_friendly_name: comBrand?.brand_friendly_name,
                    });
                  } else {
                    comBrand?.competitorList?.forEach((competitorBrand) => {
                      updatedComBrandList.push(competitorBrand);
                    });
                  }
                  globalBrandChangeInShare = true;
                } else {
                  if (sectionComprtitorBrandType === false) {
                    let parentB = defaultBrands?.selected_brand_ids?.[0];
                    updatedComBrandList.push({
                      brand_id: parentB?.brand_id,
                      brand_name: parentB?.brand_name,
                      brand_friendly_name: parentB?.brand_friendly_name,
                    });
                  } else {
                    defaultBrands?.selected_comp_brand_ids?.forEach(
                      (competitorBrand) => {
                        updatedComBrandList.push(competitorBrand);
                      }
                    );
                  }
                }
              } else {
                sectionComprtitorBrandTypeExists = false;
              }
            } else {
              let comBrand = global_brands?.columns?.[0]?.competitor
                ? global_brands?.columns?.[0]
                : global_brands?.[0]?.competitor
                ? global_brands?.[0]
                : global_brands?.competitor
                ? global_brands
                : null;
              if (comBrand?.competitor) {
                if (sectionComprtitorBrandType === false) {
                  updatedComBrandList.push({
                    brand_id: comBrand?.name,
                    brand_name: comBrand?.brand_name,
                    brand_friendly_name: comBrand?.brand_friendly_name,
                  });
                } else {
                  comBrand?.competitorList?.forEach((competitorBrand) => {
                    updatedComBrandList.push(competitorBrand);
                  });
                }
              } else {
                sectionComprtitorBrandTypeExists = false;
              }
            }
          }
          // here calculating the public_engagement_only for the deepdive
          let public_engagement_only =
            pathName === "CommandCenter"?.toLowerCase() ||
            isDeepDiveDashboardView
              ? false // for now static false sending for command center
              : shareBrands
              ? shareTemplateData?.template_type === "competition"
                ? shareTemplateData?.public_engagement_only || false
                : false
              : panes?.[index]?.template_type === "competition"
              ? panes?.[index]?.public_engagement_only || false
              : false;
          let graphObject = {
            start_date: widgets_is_locked
              ? widget_metadata?.start_date
                ? widget_metadata?.start_date
                : global_duration?.from
                ? global_duration?.from
                : defaultDuration?.from
              : Section_is_locked
              ? section_metadata.start_date
                ? section_metadata?.start_date
                : global_duration?.from
                ? global_duration?.from
                : defaultDuration?.from
              : global_duration?.from
              ? global_duration?.from
              : defaultDuration?.from,
            end_date: widgets_is_locked
              ? widget_metadata?.end_date
                ? widget_metadata?.end_date
                : global_duration?.to
                ? global_duration?.to
                : defaultDuration?.to
              : Section_is_locked
              ? section_metadata?.end_date
                ? section_metadata?.end_date
                : global_duration?.to
                ? global_duration?.to
                : defaultDuration?.to
              : global_duration?.to
              ? global_duration?.to
              : defaultDuration?.to,
            brands: shareBrands
              ? widget_metadata?.is_locked
                ? widget_brands?.columns
                  ? widget_brands?.columns
                  : section_metadata?.is_locked
                  ? section_brands?.columns
                    ? section_brands?.columns
                    : widget_metadata?.duplicate_for_brands
                    ? widget_metadata?.com_brand_ids
                    : sectionComprtitorBrandTypeExists
                    ? updatedComBrandList
                    : defaultBrands
                  : widget_metadata?.duplicate_for_brands
                  ? widget_metadata?.com_brand_ids
                  : sectionComprtitorBrandTypeExists
                  ? updatedComBrandList
                  : defaultBrands
                : section_metadata?.is_locked
                ? section_brands?.columns
                  ? section_brands?.columns
                  : widget_metadata?.duplicate_for_brands
                  ? widget_metadata?.com_brand_ids
                  : sectionComprtitorBrandTypeExists
                  ? updatedComBrandList
                  : defaultBrands
                : widget_metadata?.duplicate_for_brands
                ? widget_metadata?.com_brand_ids
                : sectionComprtitorBrandTypeExists
                ? updatedComBrandList
                : defaultBrands
              : widgets_is_locked
              ? widget_metadata?.brand_ids || widget_metadata?.brand_groups
                ? widget_brands?.columns
                : Section_is_locked
                ? section_metadata?.brand_ids || section_metadata?.brand_groups
                  ? section_brands?.columns
                  : widget_metadata?.duplicate_for_brands
                  ? widget_metadata?.com_brand_ids
                  : sectionComprtitorBrandTypeExists
                  ? updatedComBrandList
                  : global_brands
                  ? global_brands
                  : brandlogicalgroup?.length > 0
                  ? brandlogicalgroup
                  : brands
                : widget_metadata?.duplicate_for_brands
                ? widget_metadata?.com_brand_ids
                : sectionComprtitorBrandTypeExists
                ? updatedComBrandList
                : global_brands
                ? global_brands
                : brandlogicalgroup?.length > 0
                ? brandlogicalgroup
                : brands
              : Section_is_locked
              ? section_metadata?.brand_ids || section_metadata?.brand_groups
                ? section_brands?.columns
                : widget_metadata?.duplicate_for_brands
                ? widget_metadata?.com_brand_ids
                : sectionComprtitorBrandTypeExists
                ? updatedComBrandList
                : global_brands
                ? global_brands
                : brandlogicalgroup?.length > 0
                ? brandlogicalgroup
                : brands
              : widget_metadata?.duplicate_for_brands
              ? widget_metadata?.com_brand_ids
              : sectionComprtitorBrandTypeExists
              ? updatedComBrandList
              : global_brands
              ? global_brands
              : brandlogicalgroup?.length > 0
              ? brandlogicalgroup
              : brands,
            chart_type: type,
            // isBrandModified:
            //   !shareBrands &&
            //   (widgets_is_locked || Section_is_locked) &&
            //   (section_metadata?.brand_ids ||
            //     section_metadata?.brand_groups ||
            //     widget_metadata?.brand_ids ||
            //     widget_metadata?.brand_groups)
            //     ? true
            //     : global_brands
            //     ? true
            //     : false,
            deepdive_on: {
              x_axis:
                widget_metadata?.chart?.chart_type === "survey-card"
                  ? {
                      attribute: "survey_label",
                      value: survey_card_post_text,
                      trace_name: "Question",
                    }
                  : (widget_metadata?.x_axis?.attribute ||
                      widget_metadata?.primary) &&
                    type !== "sankey" &&
                    !widget_metadata?.x_axis?.config_filters?.length
                  ? // [DEFAULT] to handle x-axis in charts (!sankey) without journey attribute
                    {
                      date_part:
                        widget_metadata?.x_axis?.date_part &&
                        widget_metadata?.x_axis?.date_part !== "D"
                          ? widget_metadata?.x_axis?.date_part
                          : widget_metadata?.x_axis?.date_aggregation,
                      special_cases:
                        widget_metadata?.x_axis?.special_cases ||
                        widget_metadata?.special_cases,
                      attribute: widget_metadata?.x_axis?.attribute
                        ? widget_metadata?.x_axis?.attribute
                        : widget_metadata?.primary,
                      value:
                        widget_metadata?.chart?.chart_type === "pie" &&
                        widget_metadata?.y_axes?.length > 0
                          ? Array?.isArray(metaWidgetGraphData)
                            ? metaWidgetGraphData?.[0]?.breakdown_value
                            : metaWidgetGraphData?.breakdown_value
                          : widget_metadata?.chart?.chart_type === "sunburst" ||
                            widget_metadata?.graph_type === "sunburst"
                          ? single?.find(
                              (e) => e?.id === cordinates?.options?.parent
                            )?.name
                          : widget_metadata?.chart?.chart_type === "pie" ||
                            widget_metadata?.chart?.chart_type ===
                              "wordcloud" ||
                            widget_metadata?.chart?.chart_type === "donut" ||
                            widget_metadata?.graph_type === "pie" ||
                            widget_metadata?.graph_type === "wordcloud" ||
                            widget_metadata?.graph_type === "donut"
                          ? cordinates?.options?.name
                          : widget_metadata?.chart?.chart_type === "heatmap" ||
                            widget_metadata?.graph_type === "heatmap"
                          ? cordinates.xCategoryValue
                            ? cordinates?.xCategoryValue
                            : single[0]?.x[cordinates?.options?.x]
                          : widget_metadata?.chart?.chart_type === "scatter" ||
                            widget_metadata?.graph_type === "scatter"
                          ? widget_metadata?.y_axes?.length > 0
                            ? cordinates?.options?.name
                              ? cordinates?.options?.name
                              : cordinates?.name
                            : cordinates?.series?.name
                          : widget_metadata?.chart?.chart_type === "treemap" ||
                            widget_metadata?.graph_type === "treemap"
                          ? widget_metadata?.y_axes?.length > 0
                            ? cordinates?.node?.parentNode?.name
                              ? cordinates?.node?.parentNode?.name
                              : single?.[0]?.find(
                                  (e) => e?.id === cordinates?.options?.parent
                                )?.name
                            : cordinates?.options?.name
                            ? cordinates?.options?.name
                            : cordinates?.series?.name
                          : cordinates?.category?.toString(),
                      trace_name:
                        widget_metadata?.x_axis?.trace_name ||
                        allAttributes?.[
                          widget_metadata?.x_axis?.attribute
                            ? widget_metadata?.x_axis?.attribute
                            : widget_metadata?.primary
                        ]
                          ? widget_metadata?.x_axis?.trace_name ||
                            allAttributes?.[
                              widget_metadata?.x_axis?.attribute
                                ? widget_metadata?.x_axis?.attribute
                                : widget_metadata?.primary
                            ]
                          : "",
                    }
                  : widget_metadata?.chart?.chart_type === "sankey" &&
                    widget_metadata?.x_axis?.config_filters?.length
                  ? // to handle x-axis in sankey for journey attributes
                    {
                      date_part:
                        widget_metadata?.x_axis?.date_part &&
                        widget_metadata?.x_axis?.date_part !== "D"
                          ? widget_metadata?.x_axis?.date_part
                          : widget_metadata?.x_axis?.date_aggregation,
                      special_cases:
                        widget_metadata?.x_axis?.special_cases ||
                        widget_metadata?.special_cases,
                      attribute: getConfigFilters(
                        cordinates?.options?.from,
                        widget_metadata,
                        "attribute"
                      ),
                      value: getSankeyNode(
                        cordinates?.options?.from,
                        single?.nodes
                      )?.display_name,
                      trace_name:
                        getSankeyNode(cordinates?.options?.from, single?.nodes)
                          ?.trace_name ||
                        allAttributes[
                          getSankeyNode(
                            cordinates?.options?.from,
                            single?.nodes
                          )?.attribute
                        ],
                      config_filters: getConfigFilters(
                        cordinates?.options?.from,
                        widget_metadata,
                        "config_filters"
                      ),
                    }
                  : // to handle x-axis in other chart types for journey attributes
                  widget_metadata?.chart?.chart_type !== "sankey" &&
                    widget_metadata?.x_axis?.config_filters?.length
                  ? {
                      date_part:
                        widget_metadata?.x_axis?.date_part &&
                        widget_metadata?.x_axis?.date_part !== "D"
                          ? widget_metadata?.x_axis?.date_part
                          : widget_metadata?.x_axis?.date_aggregation,
                      special_cases:
                        widget_metadata?.x_axis?.special_cases ||
                        widget_metadata?.special_cases,
                      attribute:
                        widget_metadata?.x_axis?.config_filters?.[0]?.attribute,
                      value:
                        widget_metadata?.chart?.chart_type === "pie" &&
                        widget_metadata?.y_axes?.length > 0
                          ? Array?.isArray(metaWidgetGraphData)
                            ? metaWidgetGraphData?.[0]?.breakdown_value
                            : metaWidgetGraphData?.breakdown_value
                          : widget_metadata?.chart?.chart_type === "sunburst" ||
                            widget_metadata?.graph_type === "sunburst"
                          ? single?.find(
                              (e) => e?.id === cordinates?.options?.parent
                            )?.name
                          : widget_metadata?.chart?.chart_type === "pie" ||
                            widget_metadata?.chart?.chart_type ===
                              "wordcloud" ||
                            widget_metadata?.chart?.chart_type === "donut" ||
                            widget_metadata?.graph_type === "pie" ||
                            widget_metadata?.graph_type === "wordcloud" ||
                            widget_metadata?.graph_type === "donut"
                          ? cordinates?.options?.name
                          : widget_metadata?.chart?.chart_type === "heatmap" ||
                            widget_metadata?.graph_type === "heatmap"
                          ? single[0]?.x[cordinates?.options?.x]
                          : widget_metadata?.chart?.chart_type === "treemap" ||
                            widget_metadata?.graph_type === "treemap"
                          ? widget_metadata?.y_axes?.length > 0
                            ? cordinates?.node?.parentNode?.name
                              ? cordinates?.node?.parentNode?.name
                              : single?.[0]?.find(
                                  (e) => e?.id === cordinates?.options?.parent
                                )?.name
                            : cordinates?.options?.name
                            ? cordinates?.options?.name
                            : cordinates?.series?.name
                          : cordinates?.category?.toString(),
                      trace_name:
                        widget_metadata?.x_axis?.trace_name ||
                        allAttributes[widget_metadata?.x_axis?.attribute],
                      config_filters: widget_metadata?.x_axis?.config_filters,
                    }
                  : undefined,
              y_series: ySeriesObjForDeepDiveOn,
              y_series_secondary:
                widget_metadata?.chart?.chart_type === "scatter" ||
                widget_metadata?.graph_type === "scatter"
                  ? {
                      filters: ySeriesInWidgetMetadata
                        ? ySeriesInWidgetMetadata[1]?.filters?.length > 0
                          ? ySeriesInWidgetMetadata[1]?.filters
                          : []
                        : [],
                      attribute: ySeriesInWidgetMetadata
                        ? ySeriesInWidgetMetadata[1]?.attribute
                          ? ySeriesInWidgetMetadata[1]?.attribute
                          : null
                        : null,
                      value: cordinates?.options?.y
                        ? cordinates?.options?.y
                        : cordinates?.y,
                      trace_name:
                        ySeriesInWidgetMetadata?.[1]?.trace_name ||
                        allAttributes?.[
                          ySeriesInWidgetMetadata
                            ? ySeriesInWidgetMetadata[1]?.attribute
                              ? ySeriesInWidgetMetadata[1]?.attribute
                              : null
                            : ""
                        ]
                          ? ySeriesInWidgetMetadata?.[1]?.trace_name ||
                            allAttributes?.[
                              ySeriesInWidgetMetadata
                                ? ySeriesInWidgetMetadata[1]?.attribute
                                  ? ySeriesInWidgetMetadata[1]?.attribute
                                  : null
                                : ""
                            ]
                          : "",
                    }
                  : undefined,
              y_axis:
                (widget_metadata?.chart?.chart_type === "scatter" ||
                  widget_metadata?.graph_type === "scatter") &&
                widget_metadata?.y_axes?.[0]?.attribute
                  ? {
                      filters: [],
                      attribute: widget_metadata?.y_axes
                        ? widget_metadata?.y_axes[0]?.attribute
                        : widget_metadata?.secondary,
                      value: cordinates?.series?.name,
                      trace_name:
                        widget_metadata?.y_axes?.[0]?.trace_name ||
                        allAttributes?.[
                          widget_metadata?.y_axes
                            ? widget_metadata?.y_axes[0]?.attribute
                            : widget_metadata?.secondary
                        ]
                          ? widget_metadata?.y_axes?.[0]?.trace_name ||
                            allAttributes?.[
                              widget_metadata?.y_axes
                                ? widget_metadata?.y_axes[0]?.attribute
                                : widget_metadata?.secondary
                            ]
                          : "",
                    }
                  : widget_metadata?.chart?.chart_type === "survey-card"
                  ? {
                      filters: [],
                      attribute: "survey_label_value",
                      value: cordinates?.options?.name
                        ? cordinates?.options?.name
                        : cordinates?.category?.toString(),
                      trace_name: "Response",
                    }
                  : widget_metadata?.chart?.chart_type === "wordcloud" &&
                    widget_metadata?.chart?.chart_type !== "sankey" &&
                    widget_metadata?.chart?.chart_settings?.wordcloud_color ===
                      "1" &&
                    cordinates?.options?.SentimentType
                  ? {
                      attribute: "SentimentType",
                      trace_name: "Sentiment",
                      value: cordinates?.options?.SentimentType,
                    }
                  : y_axis_att &&
                    !checkAttType.includes(cordinates?.series?.name) &&
                    (JSON.stringify(widget_metadata?.y_axes) !== {} ||
                      JSON.stringify(widget_metadata?.secondary) !== {}) &&
                    widget_metadata?.chart?.chart_type !== "sankey" &&
                    (widget_metadata?.y_axes || widget_metadata?.secondary)
                  ? {
                      date_part: widget_metadata?.y_axes
                        ? widget_metadata?.y_axes[0]?.date_part &&
                          widget_metadata?.y_axes[0]?.date_part !== "D"
                          ? widget_metadata?.y_axes[0]?.date_part
                          : widget_metadata?.y_axes[0]?.date_aggregation
                          ? widget_metadata?.y_axes[0]?.date_aggregation
                          : null
                        : null,
                      special_cases: widget_metadata?.y_axes
                        ? widget_metadata?.y_axes[0]?.special_cases ||
                          widget_metadata?.special_cases
                        : widget_metadata?.special_cases,
                      attribute: widget_metadata?.y_axes
                        ? widget_metadata?.y_axes[0]?.attribute
                        : widget_metadata?.secondary,
                      value:
                        widget_metadata?.chart?.chart_type === "sunburst" ||
                        widget_metadata?.graph_type === "sunburst" ||
                        widget_metadata?.chart?.chart_type === "treemap" ||
                        widget_metadata?.graph_type === "treemap" ||
                        (widget_metadata?.chart?.chart_type === "pie" &&
                          widget_metadata?.y_axes?.length > 0)
                          ? cordinates?.options?.name
                          : widget_metadata?.chart?.chart_type ===
                              "wordcloud" ||
                            widget_metadata?.graph_type === "wordcloud"
                          ? single?.filter((el) => {
                              return el?.data?.find(
                                (e) => e?.value === cordinates?.options?.weight
                              );
                            })[0]?.name
                          : widget_metadata?.chart?.chart_type === "heatmap" ||
                            widget_metadata?.graph_type === "heatmap"
                          ? cordinates.yCategoryValue
                            ? cordinates?.yCategoryValue
                            : single[0]?.y[cordinates?.options?.y]
                          : cordinates?.series?.name,
                      trace_name:
                        widget_metadata?.y_axes?.[0]?.trace_name ||
                        allAttributes?.[
                          widget_metadata?.y_axes
                            ? widget_metadata?.y_axes[0]?.attribute
                            : widget_metadata?.secondary
                        ]
                          ? widget_metadata?.y_axes?.[0]?.trace_name ||
                            allAttributes?.[
                              widget_metadata?.y_axes
                                ? widget_metadata?.y_axes[0]?.attribute
                                : widget_metadata?.secondary
                            ]
                          : "",
                    }
                  : widget_metadata?.chart?.chart_type === "sankey"
                  ? {
                      attribute: getConfigFilters(
                        cordinates?.options?.to,
                        widget_metadata,
                        "attribute"
                      ),
                      value: getSankeyNode(
                        cordinates?.options?.to,
                        single?.nodes
                      ).display_name,
                      trace_name:
                        getSankeyNode(cordinates?.options?.to, single?.nodes)
                          ?.trace_name ||
                        allAttributes[
                          getSankeyNode(cordinates?.options?.to, single?.nodes)
                            ?.attribute
                        ],
                      config_filters: getConfigFilters(
                        cordinates?.options?.to,
                        widget_metadata,
                        "config_filters"
                      ),
                    }
                  : // to handle y-axes in other chart types for journey attributes
                  widget_metadata?.chart?.chart_type !== "sankey" &&
                    widget_metadata?.y_axes?.[0]?.config_filters?.length
                  ? {
                      attribute:
                        widget_metadata?.y_axes?.[0]?.config_filters?.[0]
                          ?.attribute,
                      value:
                        widget_metadata?.chart?.chart_type === "sunburst" ||
                        widget_metadata?.graph_type === "sunburst" ||
                        widget_metadata?.chart?.chart_type === "treemap" ||
                        widget_metadata?.graph_type === "treemap" ||
                        (widget_metadata?.chart?.chart_type === "pie" &&
                          widget_metadata?.y_axes?.length > 0)
                          ? cordinates?.options?.name
                          : widget_metadata?.chart?.chart_type ===
                              "wordcloud" ||
                            widget_metadata?.graph_type === "wordcloud"
                          ? single?.filter((el) => {
                              return el?.data?.find(
                                (e) => e?.value === cordinates?.options?.weight
                              );
                            })[0]?.name
                          : widget_metadata?.chart?.chart_type === "heatmap" ||
                            widget_metadata?.graph_type === "heatmap"
                          ? cordinates.yCategoryValue
                            ? cordinates?.yCategoryValue
                            : single[0]?.y[cordinates?.options?.y]
                          : cordinates?.series?.name,
                      trace_name:
                        widget_metadata?.y_axes?.[0]?.config_filters?.[0]
                          ?.trace_name ||
                        allAttributes[
                          widget_metadata?.y_axes?.[0]?.config_filters?.[0]
                            ?.attribute
                        ],
                      config_filters: [
                        widget_metadata?.y_axes?.[0]?.config_filters?.[0],
                      ],
                    }
                  : undefined,
              combined_config_filters:
                widget_metadata?.chart?.chart_type === "sankey" ||
                widget_metadata?.x_axis?.config_filters?.length
                  ? getConfigFilters(
                      cordinates,
                      widget_metadata,
                      "combineConfigFilter"
                    )
                  : null,
              deep_dive_id: widget_metadata?.deep_dive_id
                ? widget_metadata?.deep_dive_id
                : null,
              widget_inner_id: cordinates?.options?.widget_inner_id
                ? cordinates?.options?.widget_inner_id
                : null,
              name:
                widget_metadata?.chart?.chart_type === "survey-card"
                  ? null
                  : cordinates?.options?.name
                  ? cordinates?.options?.name
                  : null,
            },
            filters: filter ? filter : null,
            paneIndex: index,
            panes: panes,
            shareBrands: shareBrands,
            shareTemplateData: {
              ...shareTemplateData,
              isDeepDive: true,
              deep_dive: {
                infoBar: null,
                mentions: null,
                wordcloud: [],
                category: [],
                top_users: [],
                influencers: [],
                source_of_post: [],
                locations: [],
              },
            },
            tabName:
              workspace_privileges?.environment === 2 ||
              share_workspace_privileges?.environment === 2
                ? "Word Cloud"
                : "Mentions",
            shareCategory: shareCategory,
            filter_by: widget_metadata?.payload?.filter_by,
            filter_dropdown: widget_metadata?.payload?.filter_dropdown || [],
            filter_by_dropdown:
              widget_metadata?.payload?.filter_by_dropdown || [],
            screen: pathName === "CommandCenter"?.toLowerCase() ? screen : null,
            isTicketBased:
              widget_ids?.includes("6.1") || widget_ids?.includes("9.1"),
            ticketDeepDiveWidgetId: widget_ids?.includes("9.1")
              ? "9.1"
              : widget_ids?.includes("6.1")
              ? "6.1"
              : null,
            isWorkFlowBased: widget_ids?.includes("8.1"),
            isSurveyCardBased: isSurveyCardChart ? true : false,
            public_engagement_only: public_engagement_only ? true : false,
          };
          graphObject.isSectionCompetitorBrandTypeExists = (() => {
            // Return false if any of these conditions are true
            if (
              widget_metadata?.duplicate_for_brands ||
              (shareBrands &&
                widget_metadata?.is_locked &&
                widget_brands?.columns) ||
              (shareBrands &&
                section_metadata?.is_locked &&
                section_brands?.columns) ||
              (!shareBrands &&
                widgets_is_locked &&
                (widget_metadata?.brand_ids ||
                  widget_metadata?.brand_groups)) ||
              (!shareBrands &&
                Section_is_locked &&
                (section_metadata?.brand_ids || section_metadata?.brand_groups))
            ) {
              return false;
            }

            // Return true only if sectionComprtitorBrandTypeExists is true and globalBrandChangeInShare is true
            return (
              (sectionComprtitorBrandTypeExists && globalBrandChangeInShare) ||
              false
            );
          })();
          graphObject.isBrandModified =
            !shareBrands &&
            (global_brands ||
              (widgets_is_locked &&
                (widget_brands?.columns
                  ? widget_brands?.columns?.length
                  : widget_brands?.length)) ||
              (Section_is_locked &&
                (section_brands?.columns
                  ? section_brands?.columns?.length
                  : section_brands?.length)) ||
              widget_metadata?.duplicate_for_brands)
              ? true
              : false;
          if (isDeepDiveDashboardView) {
            dispatch(setIsDeepDiveInDashboardTemplateData(true)); // to indicate second level deepdive is opened
            // if this is second level deepdive so need to pass sub_deep_dive_on key
            // where old deepdive_on object will pass and new one is same as

            if (deepdiveDashboardData?.deepdive_on) {
              let newDeepDiveOnObject = graphObject?.deepdive_on;
              let deediveDashboardOnObject = deepdiveDashboardData?.deepdive_on
                ? { ...deepdiveDashboardData?.deepdive_on }
                : null;
              deediveDashboardOnObject = deediveDashboardOnObject
                ? deepCopy(deediveDashboardOnObject)
                : null;

              let sub_deep_dive_on = [];
              if (
                deepdiveDashboardData?.sub_deep_dive_on &&
                Array.isArray(deepdiveDashboardData.sub_deep_dive_on)
              ) {
                sub_deep_dive_on = [
                  ...sub_deep_dive_on,
                  ...deepdiveDashboardData.sub_deep_dive_on,
                ];
              }
              if (deediveDashboardOnObject) {
                sub_deep_dive_on.push(deediveDashboardOnObject);
              }
              graphObject.sub_deep_dive_on =
                sub_deep_dive_on &&
                Array.isArray(sub_deep_dive_on) &&
                sub_deep_dive_on?.length > 0
                  ? [...sub_deep_dive_on]
                  : null;
            }
          }
          dispatch(
            setIsDetailedInsightVisible({
              value: false,
              tab_id: null,
              name:
                workspace_privileges?.environment === 2 ||
                share_workspace_privileges?.environment === 2
                  ? "Word Cloud"
                  : "Mentions",
              key:
                workspace_privileges?.environment === 2 ||
                share_workspace_privileges?.environment === 2
                  ? "2"
                  : "1",
            })
          );

          dispatch(
            setTabClick({
              tabName:
                workspace_privileges?.environment === 2 ||
                share_workspace_privileges?.environment === 2
                  ? "wordcloud"
                  : "mentions",
              state: true,
            })
          );
          dispatch(setDeepDiveObject(graphObject));
          dispatch(setDeepDiveMentionsObject(graphObject));
          dispatch(setDeepDiveInfluencersObject(graphObject));
          dispatch(setMentionPageData({ data: null, page: 1, limit: 10 }));
          shareTemplateData &&
            dispatch({
              type: "SET_SHARE_TEMPLATE_DATA",
              payload: {
                ...shareTemplateData,
                isDeepDive: true,
                deep_dive: {
                  infoBar: null,
                  mentions: null,
                  wordcloud: [],
                  category: [],
                  top_users: [],
                  influencers: [],
                  source_of_post: [],
                  locations: [],
                },
              },
            });
          if (pathName === "CommandCenter"?.toLowerCase()) {
            let deep_dive_object = {
              infoBar: null,
              mentions: null,
              wordcloud: [],
              category: [],
              top_users: [],
              influencers: [],
              source_of_post: [],
              locations: [],
            };

            // Enable Deep Dive Modal
            dispatch(setScreenIsDeepDive(true, deep_dive_object));
          }
          dispatch(set_WM_DeepDive(true));
          dispatch(setDeepDiveInfoLoader(true));
          // graph/deep-dive Api Call
          dispatch(
            getDeepDive(
              authParams,
              graphObject,
              widget_ids,
              query?.get("share_id")
            )
          );
          setLoad(false);
          dispatch(setOptionHighChart(null));
          window.scrollTo(0, 0);
        } else if (
          (widget_metadata?.deep_dive_id === "ai-actionable" &&
            !cordinates?.options?.widget_inner_id) ||
          cordinates?.options?.value <= 0
        ) {
          // Deep dive on organization chart
          // console.log("Inside Else If");
        } else if (
          widget_metadata?.chart?.chart_type === "sankey" &&
          (!cordinates?.from || !cordinates?.to)
        ) {
          callNotification(
            "Deep dive on this part of the widget is not available",
            "info"
          );
        } else {
          callNotification("Deep dive on this widget is not available", "info");
        }
      }
    }
  };

  const callChartApi = (exclude_words) => {
    if (authParams?.screen_id) {
      dispatch(
        updateScreenWidgetPropdataWithClearGraphData(
          section_index >= 0 ? section_index : null,
          widget_index,
          {
            prop_name: "temp_exclude_words",
            prop_value: getUniqueValues(exclude_words),
          }
        )
      );
    } else {
      let metadata = keywordData?.widget_metadata;

      let duration = panes?.[index]?.duration;

      // let global_brands = getGlobalBrands(
      //   panes[index]?.brands,
      //   global_dashboard_single_brands,
      //   global_dashboard_brands,
      //   panes[index]?.template_type
      // );
      let global_brands = panes?.[index]?.brands;

      let widgetObj = {
        brand_groups: metadata?.brand_groups,
        singleBrands: brands,
      };

      let filter = getAllFilters(
        graphConditionConfig,
        panes,
        index,
        section_index,
        widget_index,
        brandlogicalgroup,
        metadata?.brand_ids, //logical brand change
        widgetObj
      );

      let obj = {};
      obj.section_index = section_index;
      obj.widget_index = widget_index;
      obj.data = metadata;
      obj.panes = panes;
      obj.paneIndex = index;
      obj.duration = duration;
      obj.global_brands = global_brands;
      obj.filter = filter;
      obj.temporary_words = temporary_words;
      obj.brandlogicalgroup = brandlogicalgroup;
      obj.brands = brands;
      obj.graphConditionConfig = graphConditionConfig;
      obj.authParams = authParams;
      obj.exclude_words = exclude_words;
      obj.type = "view";
      obj.filter_dropdown = metadata?.filter_dropdown
        ? metadata?.filter_dropdown
        : [];
      obj.filter_by_dropdown = metadata?.filter_by_dropdown
        ? metadata?.filter_by_dropdown
        : [];
      obj.filter_by_dropdown_data = metadata?.filter_by_dropdown_data
        ? metadata?.filter_by_dropdown_data
        : [];
      let widgetGraphObject = getWidgetGraphObject(obj);
      widgetGraphObject.isNetworkChartV2 =
        widgetGraphObject?.chart?.chart_type === "network-graph-v2"
          ? true
          : false;
      let indexes = {
        pane_index: index,
        section_index: section_index >= 0 ? section_index : null,
        panes: panes,
        widget_index: widget_index,
        widget_id: metadata?.widget_id,
        graph_type: metadata?.graph_type,
        section_id: shareTemplateData
          ? section_index >= 0
            ? shareTemplateData?.sections?.[section_index]?.section_id
            : shareTemplateData?.section?.[0]?.section_id
          : footerValue?.template
          ? section_index >= 0
            ? Section?.[section_index]?.id
            : Section?.[0]?.id
          : section_index >= 0
          ? panes?.[index]?.sections?.[section_index]?.section_id
          : panes?.[index]?.section?.[0]?.section_id,
        template_details: templateDetails,
      };
      if (authParams?.category_id && brands) {
        widgetGraphObject?.graph_type === "aggregate"
          ? dispatch(
              setAggregateData(
                widgetGraphObject,
                indexes //widget data
              )
            )
          : widgetGraphObject?.primary
          ? dispatch(
              setGraphData(
                widgetGraphObject,
                indexes //widget data
              )
            )
          : graphConditionConfig?.[`data-source`]?.includes(
              widgetGraphObject?.data_source
            )
          ? dispatch(
              setPageChart(
                authParams,
                widgetGraphObject,
                indexes
                // updateFilter
              )
            )
          : (widgetGraphObject?.x_axis ||
              widgetGraphObject?.y_series ||
              widgetGraphObject?.chart) &&
            dispatch(
              setWidgetMakerGraphData(
                authParams,
                widgetGraphObject,
                indexes
                // updateFilter
              )
            );
      }

      let sectionObj = {
        panes: panes,
        index: index,
        section_index: section_index >= 0 ? section_index : null,

        widget_index: widget_index,

        singleBrands: brands,
      };
      metadata.temp_exclude_words = exclude_words;
      dispatch(
        setWidgetData(
          metadata,
          index,
          panes,
          section_index >= 0 ? section_index : null,
          keywordData?.section_id, //section id for only widgets
          sectionObj,
          brands
        )
      );
    }
  };

  const removeKeyword = (event, section_index, widget_index) => {
    if (
      (window?.location?.pathname === "/dashboard" ||
        window.location.pathname === "/pin-dashboard") &&
      !AiInsights
    ) {
      let shareSections = shareTemplateData?.section
        ? shareTemplateData?.section?.[0]
        : shareTemplateData?.sections?.[section_index];
      let section_metadata = shareBrands
        ? shareSections
        : section_index >= 0
        ? panes[index]?.sections?.[section_index]
        : panes[index]?.section?.[0];
      let widget_metadata = shareBrands
        ? shareSections?.widgets?.[widget_index]
        : section_index >= 0
        ? panes[index]?.sections?.[section_index]?.widgets[widget_index]
        : panes[index]?.section?.[0]?.widgets[widget_index];

      if (
        graphConditionConfig?.["wordcloud-hide-unhide"]?.includes(
          widget_metadata?.x_axis?.attribute
        )
      ) {
        let widgetId = widget_metadata?.parentId
          ? widget_metadata?.parentId
          : widget_metadata?.childId
          ? widget_metadata?.childId
          : widget_metadata?.widget_id;

        setkeywordData({
          keyword_name: event.target.point.name,
          widget_metadata: widget_metadata,
          section_index: section_index,
          widget_index: widget_index,
          template_id: panes[index]?.template_id,
          widget_id: widgetId,
          section_id: section_metadata?.section_id,
        });

        if (event.clientX > 1177) {
          setxPosition(event.clientX - 100);
        } else {
          setxPosition(event.clientX);
        }

        event.clientY && setyPosition(event.clientY);
      }
    } else if (authParams?.screen_id && !AiInsights) {
      let section_metadata =
        section_index !== null &&
        section_index !== undefined &&
        screen?.sections &&
        screen?.sections?.[section_index]
          ? screen?.sections?.[section_index]
          : section_index >= 0 &&
            screen?.sections &&
            screen?.sections?.[section_index]
          ? screen?.sections?.[section_index]
          : screen?.section && screen?.section?.[0]
          ? screen?.section?.[0]
          : null;
      let widget_metadata =
        section_index !== null &&
        section_index !== undefined &&
        screen?.sections &&
        screen?.sections?.[section_index]?.widgets?.[widget_index]
          ? screen?.sections?.[section_index]?.widgets?.[widget_index]
          : section_index >= 0 &&
            screen?.sections &&
            screen?.sections?.[section_index]
          ? screen?.sections?.[section_index]
          : screen?.section?.[0]?.widgets?.[widget_index]
          ? screen?.section?.[0]?.widgets?.[widget_index]
          : screen?.widgets && screen?.widgets?.[widget_index]
          ? screen?.widgets && screen?.widgets?.[widget_index]
          : null;
      if (
        graphConditionConfig?.["wordcloud-hide-unhide"]?.includes(
          widget_metadata?.x_axis?.attribute
        )
      ) {
        let widgetId = widget_metadata?.parentId
          ? widget_metadata?.parentId
          : widget_metadata?.childId
          ? widget_metadata?.childId
          : widget_metadata?.widget_id;

        setkeywordData({
          keyword_name: event?.target?.point?.name,
          widget_metadata: widget_metadata,
          section_index: section_index >= 0 ? section_index : 0,
          widget_index: widget_index,
          template_id: screen?.screen_id,
          widget_id: widgetId,
          section_id: section_metadata?.section_id
            ? section_metadata?.section_id
            : screen?.section_id,
        });

        if (authParams?.screen_id === "000-000-556") {
          let maxXPosition = event?.clientX > 440 ? 440 : event?.clientX;
          setxPosition(maxXPosition);
        } else {
          if (event?.clientX > 1177) {
            setxPosition(event?.clientX - 100);
          } else {
            setxPosition(event?.clientX);
          }
        }

        if (authParams?.screen_id === "000-000-556") {
          let maxYPosition =
            event?.clientY - 400 > 250 ? 250 : event?.clientY - 400;
          event?.clientY && setyPosition(maxYPosition);
        } else {
          event?.clientY && setyPosition(event?.clientY);
        }
      }
    }
  };
  const handleRemove = (e, type = "temp") => {
    let lockedBrandIds, lockedBrandGroups;
    if (
      widgetMetadData?.is_locked &&
      (widgetMetadData?.brand_ids?.length ||
        widgetMetadData?.brand_groups?.length)
    ) {
      lockedBrandIds =
        widgetMetadData?.brand_ids?.length &&
        widgetMetadData?.brand_ids.map((item) => item.brand_id);
      lockedBrandGroups =
        widgetMetadData?.brand_groups?.length &&
        widgetMetadData?.brand_groups.map((item) => item.brand_group_name);
    } else if (
      sectionMetaData?.is_locked &&
      (sectionMetaData?.brand_ids?.length ||
        sectionMetaData?.brand_groups?.length)
    ) {
      lockedBrandIds =
        sectionMetaData?.brand_ids?.length &&
        sectionMetaData?.brand_ids.map((item) => item.brand_id);
      lockedBrandGroups =
        sectionMetaData?.brand_groups?.length &&
        sectionMetaData?.brand_groups.map((item) => item.brand_group_name);
    }
    if (
      !tempModal &&
      !workspace_privileges.wc_hide_temporary &&
      type == "temp"
    ) {
      setTempModal(true);
    } else if (
      !permModal &&
      !workspace_privileges.wc_hide_permanent &&
      type == "perm"
    ) {
      setPermModal(true);
    } else if (
      !brandModal &&
      !workspace_privileges.wc_hide_brand &&
      type == "brand"
    ) {
      setBrandModal(true);
    } else if (
      type === "temp" &&
      workspace_privileges.wc_hide_temporary &&
      !tempModal
    ) {
      removeTemporarily();
    } else if (
      type === "perm" &&
      workspace_privileges.wc_hide_permanent &&
      !permModal
    ) {
      removePermanently(e);
    } else if (
      type === "brand" &&
      workspace_privileges.wc_hide_brand &&
      !brandModal
    ) {
      removePermanentlyFromBrand(e, lockedBrandIds, lockedBrandGroups);
    }
  };
  const removeTemporarily = () => {
    if (
      authParams?.screen_id === "000-000-552" ||
      authParams?.screen_id === "000-000-556"
    ) {
      dispatch(setDateAggregationLoader(true));
    }

    setxPosition(0);
    setyPosition(0);
    let widget_id = keywordData?.widget_id;
    //for exploded wordcloud by comptitor brands
    if (keywordData?.widget_metadata?.duplicate_for_brands) {
      widget_id = getTemporaryWordWidgetId(keywordData?.widget_metadata);
    }
    let update_temporary_words = { ...temporary_words };
    if (!update_temporary_words?.[keywordData?.template_id]) {
      update_temporary_words[keywordData?.template_id] = {
        [keywordData.section_id]: {
          [widget_id]: {
            exclude_words: [keywordData?.keyword_name],
          },
        },
      };
    } else {
      if (
        !update_temporary_words?.[keywordData?.template_id]?.[
          keywordData?.section_id
        ]
      ) {
        update_temporary_words[keywordData?.template_id][
          keywordData.section_id
        ] = {
          [widget_id]: {
            exclude_words: [keywordData?.keyword_name],
          },
        };
      } else {
        if (
          !update_temporary_words?.[keywordData?.template_id]?.[
            keywordData?.section_id
          ]?.[widget_id]
        ) {
          update_temporary_words[keywordData?.template_id][
            keywordData.section_id
          ][widget_id] = {
            exclude_words: [keywordData?.keyword_name],
          };
        } else {
          if (
            update_temporary_words?.[keywordData?.template_id]?.[
              keywordData?.section_id
            ]?.[widget_id]?.exclude_words
          ) {
            let words =
              update_temporary_words?.[keywordData?.template_id]?.[
                keywordData?.section_id
              ]?.[widget_id]?.exclude_words;
            update_temporary_words[keywordData?.template_id][
              keywordData?.section_id
            ][widget_id].exclude_words = [...words, keywordData?.keyword_name];
          }
        }
      }
    }

    dispatch(setTemporaryWords(update_temporary_words));
    setTimeout(() => {
      callChartApi(
        update_temporary_words?.[keywordData?.template_id]?.[
          keywordData?.section_id
        ]?.[widget_id]?.exclude_words
      );
    }, 0);

    if (authParams.screen_id && modalCheckboxValue) {
      let paneObject = {
        ...keywordData,
        screen: screen,
        exclude_words: null,
        include_words: [],
        brands: null,
        singleBrands: null,
        isBrandModified: false,
        temporary_words: null,
        widget_metadata: null,
        comp_exclude_words: null,
        competitorBrands: null,
        wc_hide_temporary: modalCheckboxValue
          ? true
          : workspace_privileges.wc_hide_temporary
          ? true
          : false,
        wc_hide_permanent: workspace_privileges.wc_hide_permanent
          ? true
          : false,
        wc_hide_brand: workspace_privileges.wc_hide_brand ? true : false,
      };
      dispatch(
        setCommandCenterWordcloudModifyWords(
          authParams,
          paneObject,
          modalCheckboxValue,
          "hidewordtemporarily"
        )
      );
    } else {
      // To Update temp_hidden key at backend only
      let paneObject = {
        ...keywordData,
        panes: panes,
        index: index,
        activeKey: activeKey,
        exclude_words: null,
        include_words: [],
        brands: null,
        singleBrands: null,
        isBrandModified: false,
        temporary_words: null,
        widget_metadata: null,
        comp_exclude_words: null,
        competitorBrands: null,
        template_id: null,
        widget_id: null,
        section_id: null,
        wc_hide_temporary: modalCheckboxValue
          ? true
          : workspace_privileges.wc_hide_temporary
          ? true
          : false,
        wc_hide_permanent: workspace_privileges.wc_hide_permanent
          ? true
          : false,
        wc_hide_brand: workspace_privileges.wc_hide_brand ? true : false,
      };
      if (modalCheckboxValue) {
        dispatch(
          setWordcloudModifyWords(
            authParams,
            paneObject,
            modalCheckboxValue,
            "hidewordtemporarily"
          )
        );
      }
    }
  };

  const removePermanently = (event) => {
    if (
      authParams?.screen_id === "000-000-552" ||
      authParams?.screen_id === "000-000-556"
    ) {
      dispatch(setDateAggregationLoader(true));
    }
    event.preventDefault();

    let comp_exclude_words = [];
    if (keywordData?.widget_metadata?.duplicate_for_brands) {
      keywordData.widget_id = keywordData?.widget_metadata?.parentId
        ? keywordData?.widget_metadata?.parentId
        : keywordData?.widget_metadata?.childId
        ? keywordData?.widget_metadata?.childId
        : keywordData?.widget_metadata?.widget_id;
      let brand_id =
        keywordData?.widget_metadata?.payload?.brand_list?.[0]?.brand_id;
      comp_exclude_words.push({
        [brand_id]: keywordData?.keyword_name
          ? [keywordData?.keyword_name]
          : [],
      });
    }
    if (authParams?.screen_id) {
      let paneObject = {
        ...keywordData,
        screen: screen,
        exclude_words: keywordData?.keyword_name
          ? [keywordData?.keyword_name]
          : [],
        brands: brandlogicalgroup?.length > 0 ? brandlogicalgroup : brands,
        singleBrands: brands,
        isBrandModified: false,
        temporary_words: temporary_words,
        widget_metadata: keywordData?.widget_metadata,
        widget_uuid: keywordData?.widget_metadata?.widget_uuid,
        comp_exclude_words: comp_exclude_words,
        competitorBrands: competitorBrands,
        wc_hide_temporary: workspace_privileges.wc_hide_temporary
          ? true
          : false,
        wc_hide_permanent: modalCheckboxValue
          ? true
          : workspace_privileges.wc_hide_permanent
          ? true
          : false,
        wc_hide_brand: workspace_privileges.wc_hide_brand ? true : false,
      };
      dispatch(
        setCommandCenterWordcloudModifyWords(
          authParams,
          paneObject,
          modalCheckboxValue,
          "hidewordpermanently"
        )
      );
    } else {
      let paneObject = {
        ...keywordData,
        panes: panes,
        index: index,
        activeKey: activeKey,
        exclude_words: keywordData?.keyword_name
          ? [keywordData?.keyword_name]
          : [],
        brands: brandlogicalgroup?.length > 0 ? brandlogicalgroup : brands,
        singleBrands: brands,
        isBrandModified: false,
        temporary_words: temporary_words,
        widget_metadata: keywordData?.widget_metadata,
        widget_uuid: keywordData?.widget_metadata?.widget_uuid,
        comp_exclude_words: comp_exclude_words,
        competitorBrands: competitorBrands,
        wc_hide_temporary: workspace_privileges.wc_hide_temporary
          ? true
          : false,
        wc_hide_permanent: modalCheckboxValue
          ? true
          : workspace_privileges.wc_hide_permanent
          ? true
          : false,
        wc_hide_brand: workspace_privileges.wc_hide_brand ? true : false,
      };
      dispatch(
        setWordcloudModifyWords(
          authParams,
          paneObject,
          modalCheckboxValue,
          "hidewordpermanently"
        )
      );
    }
    setxPosition(0);
    setyPosition(0);
  };

  const removePermanentlyFromBrand = (
    event,
    lockedBrandIds,
    lockedBrandGroups
  ) => {
    if (
      authParams?.screen_id === "000-000-552" ||
      authParams?.screen_id === "000-000-556"
    ) {
      dispatch(setDateAggregationLoader(true));
    }
    event.preventDefault();

    let comp_exclude_words = [];
    if (authParams?.screen_id) {
      let brand_id = null;
      let brandIdsOrGroups;
      let brandGroups;
      if (keywordData?.widget_metadata?.duplicate_for_brands) {
        keywordData.widget_id = keywordData?.widget_metadata?.parentId
          ? keywordData?.widget_metadata?.parentId
          : keywordData?.widget_metadata?.childId
          ? keywordData?.widget_metadata?.childId
          : keywordData?.widget_metadata?.widget_id;
        brand_id =
          keywordData?.widget_metadata?.payload?.brand_list?.[0]?.brand_id;
        comp_exclude_words.push({
          [brand_id]: keywordData?.keyword_name
            ? [keywordData?.keyword_name]
            : [],
        });
        brand_id = [brand_id];
      } else {
        if (screen.brands?.columns?.length > 0) {
          let brandsColumnCopy = deepCopy(screen?.brands?.columns);
          brandIdsOrGroups = brandsColumnCopy?.map((brand) => brand?.name);
          brandGroups = brandsColumnCopy[0]?.group;
        } else if (screen?.brands?.length > 0) {
          let brandsCopy = deepCopy(screen?.brands);
          brandIdsOrGroups = brandsCopy?.map(
            (brand) => brand?.name || brand?.brand_id
          );
          brandGroups = brandsCopy[0]?.group;
        }
        if (!brandGroups) {
          brand_id = [...brandIdsOrGroups];
        }
      }
      let paneObject = {
        ...keywordData,
        screen: screen,
        exclude_words: keywordData?.keyword_name
          ? [keywordData?.keyword_name]
          : [],
        brands: brandlogicalgroup?.length > 0 ? brandlogicalgroup : brands,
        singleBrands: brands,
        isBrandModified: false,
        temporary_words: temporary_words,
        widget_metadata: keywordData?.widget_metadata,
        widget_uuid: keywordData?.widget_metadata?.widget_uuid,
        comp_exclude_words: [],
        competitorBrands: competitorBrands,
        brand_id: brand_id,
        brand_group: brandGroups ? [...brandIdsOrGroups] : null,
        wc_hide_temporary: workspace_privileges.wc_hide_temporary
          ? true
          : false,
        wc_hide_permanent: workspace_privileges.wc_hide_permanent
          ? true
          : false,
        wc_hide_brand: modalCheckboxValue
          ? true
          : workspace_privileges.wc_hide_brand
          ? true
          : false,
      };
      dispatch(
        setCommandCenterWordcloudModifyWords(
          authParams,
          paneObject,
          modalCheckboxValue,
          "hidewordonbrandlevel"
        )
      );
    } else {
      let brand_id = null;
      let brandIdsOrGroups;
      let brandGroups;
      if (lockedBrandIds?.length || lockedBrandGroups?.length) {
        brand_id = lockedBrandIds?.length ? lockedBrandIds : null;
        brandIdsOrGroups = lockedBrandGroups?.length ? lockedBrandGroups : null;
        brandGroups = lockedBrandGroups?.length;
      } else {
        if (keywordData?.widget_metadata?.duplicate_for_brands) {
          keywordData.widget_id = keywordData?.widget_metadata?.parentId
            ? keywordData?.widget_metadata?.parentId
            : keywordData?.widget_metadata?.childId
            ? keywordData?.widget_metadata?.childId
            : keywordData?.widget_metadata?.widget_id;
          brand_id =
            keywordData?.widget_metadata?.payload?.brand_list?.[0]?.brand_id;
          comp_exclude_words.push({
            [brand_id]: keywordData?.keyword_name
              ? [keywordData?.keyword_name]
              : [],
          });
          brand_id = [brand_id];
          if (comp_exclude_words?.length) {
            brand_id = [+Object.keys(comp_exclude_words[0])[0]];
          }
        } else {
          if (panes[index].brands?.columns?.length > 0) {
            let brandsColumnCopy = deepCopy(panes[index]?.brands?.columns);
            brandIdsOrGroups = brandsColumnCopy?.map((brand) => brand?.name);
            brandGroups = brandsColumnCopy[0]?.group;
          } else if (panes[index]?.brands?.length > 0) {
            let brandsCopy = deepCopy(panes[index]?.brands);
            brandIdsOrGroups = brandsCopy?.map(
              (brand) => brand?.name || brand?.brand_id
            );
            brandGroups = brandsCopy[0]?.group;
          }
          if (!brandGroups) {
            brand_id = [...brandIdsOrGroups];
          }
        }
      }
      let paneObject = {
        ...keywordData,
        panes: panes,
        index: index,
        activeKey: activeKey,
        exclude_words: keywordData?.keyword_name
          ? [keywordData?.keyword_name]
          : [],
        brands: brandlogicalgroup?.length > 0 ? brandlogicalgroup : brands,
        singleBrands: brands,
        isBrandModified: false,
        temporary_words: temporary_words,
        widget_metadata: keywordData?.widget_metadata,
        widget_uuid: keywordData?.widget_metadata?.widget_uuid,
        comp_exclude_words: [],
        competitorBrands: competitorBrands,
        brand_id: brand_id,
        brand_group: brandGroups ? [...brandIdsOrGroups] : null,
        wc_hide_temporary: workspace_privileges.wc_hide_temporary
          ? true
          : false,
        wc_hide_permanent: workspace_privileges.wc_hide_permanent
          ? true
          : false,
        wc_hide_brand: modalCheckboxValue
          ? true
          : workspace_privileges.wc_hide_brand
          ? true
          : false,
      };
      dispatch(
        setWordcloudModifyWords(
          authParams,
          paneObject,
          modalCheckboxValue,
          "hidewordonbrandlevel"
        )
      );
    }
    setxPosition(0);
    setyPosition(0);
  };

  useEffect(() => {
    if (highChartDeepDive?.flag && id === highChartDeepDive?.id) {
      // console.log("test", highChartDeepDive?.data);
      dispatch(setHighChartDeepDive({ data: null, flag: false, id: null }));

      handleOnClickDeepDive(
        highChartDeepDive?.data,
        section_index,
        widget_index,
        single
      );
    }
  }, [highChartDeepDive]);
  useEffect(() => {
    if (highChartRightClick?.flag && id === highChartRightClick?.id) {
      dispatch(setHighChartRightClick({ data: null, flag: false, id: null }));
      removeKeyword(highChartRightClick?.data, section_index, widget_index);
    }
  }, [highChartRightClick]);
  //fix tab switch rendering time dynamic set to 0
  useEffect(() => {
    let timeout = setTimeout(() => {
      setLoad(true);
    }, 0);

    return () => clearTimeout(timeout);
  }, []);

  const isDeepDive = authParams?.screen_id
    ? screen?.isDeepDiveDashboard && !screen?.isDeepDive
      ? deepdiveDashboardTemplateData?.template_type === "deep_dive"
      : !screen?.isDeepDive
    : shareableLinkDeepDiveStatus
    ? shareTemplateData?.isDeepDiveDashboard && !shareTemplateData?.isDeepDive
      ? deepdiveDashboardTemplateData?.template_type === "deep_dive"
      : !shareTemplateData?.isDeepDive
    : panes[getPaneIndex(panes, activeKey)]?.isDeepDiveDashboard &&
      !panes[getPaneIndex(panes, activeKey)]?.isDeepDive
    ? deepdiveDashboardTemplateData?.template_type === "deep_dive"
    : !panes[getPaneIndex(panes, activeKey)]?.isDeepDive;

  const isNotTicketTrandsView = shareableLinkDeepDiveStatus
    ? !shareTemplateData?.isTicketTrandsView
    : !panes[getPaneIndex(panes, activeKey)]?.isTicketTrandsView;

  const isNotAdsCampaignDashboard = shareableLinkDeepDiveStatus
    ? shareTemplateData?.quick_filter?.attribute !== "Ads_Campaign"
    : panes[getPaneIndex(panes, activeKey)]?.quick_filter !== "Ads_Campaign";

  const clickcondition =
    ((panes[getPaneIndex(panes, activeKey)]?.predefined &&
      panes[getPaneIndex(panes, activeKey)]?.template_type !== "ticket") ||
      (!panes[getPaneIndex(panes, activeKey)]?.predefined &&
        panes[getPaneIndex(panes, activeKey)]?.isDashboard) ||
      shareableLinkDeepDiveStatus ||
      (authParams?.screen_id &&
        metadata?.chart?.chart_settings?.allow_deepdive)) &&
    !panes?.[index]?.is_widget_maker_open_obj?.open_flag &&
    (window?.location?.pathname === "/dashboard" ||
      window?.location?.pathname === "/pin-dashboard" ||
      shareableLinkDeepDiveStatus ||
      (authParams?.screen_id &&
        (metadata?.chart?.chart_settings?.allow_deepdive ||
          metadata?.widget_id === "000-100-956"))) &&
    !AiInsights &&
    !dashboardTrendsFlag &&
    isDeepDive &&
    isNotTicketTrandsView &&
    isNotAdsCampaignDashboard;

  const rightClickCondition =
    !PdfDownloadStatus &&
    isDeepDive &&
    !isDeepDiveDashboardView &&
    ((panes[getPaneIndex(panes, activeKey)]?.predefined &&
      panes[getPaneIndex(panes, activeKey)]?.template_type !== "ticket") ||
      (!panes[getPaneIndex(panes, activeKey)]?.predefined &&
        panes[getPaneIndex(panes, activeKey)]?.isDashboard) ||
      authParams?.screen_id) &&
    (window?.location?.pathname === "/dashboard" ||
      window.location.pathname === "/pin-dashboard" ||
      authParams?.screen_id) &&
    !AiInsights &&
    !dashboardTrendsFlag &&
    !isSurveyCardChart;
  // to hide remove word modal of wordcloud
  const handleEmptyClick = (e) => {
    if (refTooltip?.current && !refTooltip.current.contains(e.target)) {
      setxPosition(0);
      setyPosition(0);
    }
  };
  if (!load)
    return (
      <div
        className="d-flex align-items-center"
        style={{
          justifyContent: "space-evenly",
          height: authParams?.screen_id ? "100%" : "45rem",
        }}
      >
        <div className="lds-ring">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    );
  else
    return (
      <>
        <div
          className={`resize__chart--window ${
            PdfDownloadStatus ? "p-10" : "p-15"
          }`}
          id={id}
          onClick={(e) => handleEmptyClick(e)}
          //onClick={() => copyData(opData[id])}
        >
          {opData?.[id] && (
            <HighChartrender
              id={id}
              opData={opData?.[id]}
              clickcondition={clickcondition}
              rightClickCondition={rightClickCondition}
              dashboard={dashboard}
              pptDownloadStatus={isPPTModalOpen ? isPPTModalOpen : false}
              pdfDownloadStatus={PdfDownloadStatus}
            />
          )}
        </div>
        {opData?.[id]?.customLegend ? (
          <div className="d-flex w-100 justify-content-center flex-wrap mt-15 transform__capitalize">
            {single?.[0]?.legends?.map((el, index) => {
              return (
                <div key={index}>
                  <span
                    className="d-flex mr-10 font-weight500 text-Darkgray font-Size12 flex-wrap align-items-center"
                    key={`${el}_${index}`}
                  >
                    <span
                      style={{
                        backgroundColor: single?.[0]?.legend_colors?.[index],
                        width: "1.2rem",
                        height: "1.2rem",
                        borderRadius: "50%",
                      }}
                      className="d-inline-block mr-10"
                    />
                    {el}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          ""
        )}
        {opData && opData[id] && opData[id]?.wordCloudLegend ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              textTransform: "capitalize",
              margin: "1.5rem",
              marginRight: "0rem",
            }}
          >
            {single.map((el, index) => {
              return (
                <span
                  key={index}
                  style={{
                    color: "#444",
                    fontSize: "small",
                    fontFamily: "Roboto Slab",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "1rem",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: el.color,
                      width: "1.2rem",
                      height: "1.2rem",
                      display: "inline-block",
                      marginRight: "1rem",
                    }}
                  ></span>
                  {el.name}
                </span>
              );
            })}
          </div>
        ) : (
          ""
        )}

        <div
          className={`wordcloud_card ${xPosition == 0 && "d-none"}`}
          style={{
            left:
              !authParams?.screen_id === "000-000-556" ? xPosition : xPosition,
            top: yPosition,
            position: authParams?.screen_id === "000-000-556" ? "absolute" : "",
          }}
          ref={refTooltip}
        >
          <div className="text_regular fontSize-13 p-1 d-flex flex-column">
            <div
              className={`p-1 card_item cursor-pointer ${
                authParams?.screen_id === "000-000-556" &&
                "d-flex align-items-center"
              }`}
              onClick={(e) => handleRemove(e, "temp")}
            >
              <img
                src={hide_temporary}
                alt="hide"
                className={`mr-1 cursor-pointer remove_icon ${
                  authParams?.screen_id ? "opacity-light" : ""
                }`}
              />
              Hide temporarily from this widget
              <Tooltip title="The word will be hidden from this widget for the current session only.">
                <img
                  src={InfoIcon}
                  alt="Info Icon"
                  className="ml-1 cursor-pointer"
                />
              </Tooltip>
            </div>
            <Tooltip
              title={
                metadata?.is_widget_edited
                  ? "Action not allowed on edited widget"
                  : ""
              }
            >
              <div
                className={`p-1 card_item cursor-pointer ${
                  authParams?.screen_id === "000-000-556" &&
                  "d-flex align-items-center"
                }`}
                onClick={(event) =>
                  !metadata?.is_widget_edited && handleRemove(event, "perm")
                }
              >
                <img
                  src={hide_permanent}
                  alt="permanent"
                  className={`mr-1 cursor-pointer remove_icon ${
                    authParams?.screen_id ? "opacity-light" : ""
                  }`}
                />
                Hide permanently from this widget
                <Tooltip title="The word will be hidden from this widget permanently.">
                  <img
                    src={InfoIcon}
                    alt="Info Icon"
                    className="ml-1 cursor-pointer"
                  />
                </Tooltip>
              </div>
            </Tooltip>
            <Tooltip
              title={
                metadata?.is_widget_edited
                  ? "Action not allowed on edited widget"
                  : ""
              }
            >
              <div
                className={`p-1 card_item cursor-pointer ${
                  authParams?.screen_id === "000-000-556" &&
                  "d-flex align-items-center"
                }`}
                onClick={(event) =>
                  !metadata?.is_widget_edited && handleRemove(event, "brand")
                }
              >
                <img
                  style={{ width: "1.5rem", height: "1.5rem" }}
                  src={hide_permanent_brand}
                  alt="permanent"
                  className={`mr-1 cursor-pointer remove_icon ${
                    authParams?.screen_id ? "opacity-light" : ""
                  }`}
                />
                Hide Permanently from Brand/Logical Group(s)
                <Tooltip title="The word will be hidden from this brand permanently on all dashboards and widget maker.">
                  <img
                    src={InfoIcon}
                    alt="Info Icon"
                    className="ml-1 cursor-pointer"
                  />
                </Tooltip>
              </div>
            </Tooltip>
          </div>
        </div>
        {((permModal && !workspace_privileges.wc_hide_permanent) ||
          (tempModal && !workspace_privileges.wc_hide_temporary) ||
          (brandModal && !workspace_privileges.wc_hide_brand)) && (
          <HideKeywordModal
            tempModal={tempModal}
            permModal={permModal}
            brandModal={brandModal}
            setTempModal={setTempModal}
            setPermModal={setPermModal}
            setBrandModal={setBrandModal}
            modalCheckboxValue={modalCheckboxValue}
            setModalCheckboxValue={setModalCheckboxValue}
            removeTemporarily={removeTemporarily}
            removePermanently={removePermanently}
            removePermanentlyFromBrand={removePermanentlyFromBrand}
            setxPosition={setxPosition}
            setyPosition={setyPosition}
          />
        )}
      </>
    );
};

// Export the configured Highcharts instance for use in other files
export { Highcharts };

export default React.memo(HighCharts, isEqual);
// handleOnClickDeepDive(event)
